class App {
  constructor() {
    this.init();
  }

  init() {
    this.setVariables();

    window.addEventListener("cart::update", (event) => {
      let count = event.detail.cart?.item_count;

      if (count === undefined && event.detail.cartContents) {
        const doc = new DOMParser().parseFromString(
          event.detail.cartContents,
          "text/html",
        );
        const el = doc.querySelector("cart-contents[data-item-count]");
        if (el) count = parseInt(el.dataset.itemCount, 10);
      }

      if (count !== undefined) this._updateCartIndicators(count);
    });
  }

  setVariables() {
    const topHeaderHeight =
      document.querySelector(".header-container__top").offsetHeight || 0;
    const mainHeaderHeight =
      document.querySelector(".header-container__main").offsetHeight || 0;

    document.documentElement.style.setProperty(
      "--header-top-height",
      `${topHeaderHeight}px`,
    );
    document.documentElement.style.setProperty(
      "--header-main-height",
      `${mainHeaderHeight}px`,
    );
    document.documentElement.style.setProperty(
      "--header-height",
      `${topHeaderHeight + mainHeaderHeight}px`,
    );
    document.documentElement.style.setProperty(
      "--true-vw",
      `${document.documentElement.clientWidth}px`,
    );
  }

  _updateCartIndicators(count) {
    document.querySelectorAll("[data-cart-indicator]").forEach((el) => {
      el.dataset.cartIndicator = `${count}`;
      el.textContent = `${count}`;
    });
  }

  refresh() {
    this.setVariables();
  }
}

const app = new App();

window.addEventListener("resize", () => {
  app.refresh();
  syncFooterMenus();
});

function syncFooterMenus() {
  const isDesktop = window.matchMedia("(min-width: 992px)").matches;
  document.querySelectorAll(".footer__menu details").forEach((el) => {
    el.open = isDesktop;
  });
}
syncFooterMenus();
window.addEventListener("shopify:section:load", (event) => {
  app.refresh();
  initScrollState();

  requestAnimationFrame(() => {
    event.target
      .querySelectorAll("swiper-container[init='false']")
      .forEach((swiper) => {
        if (swiper.swiper) return;
        let node = swiper;
        while ((node = node.parentElement)) {
          if (node === event.target) break;
          if (node.localName.includes("-") && node.connectedCallback) {
            node.connectedCallback();
            break;
          }
        }
      });
  });
});
window.addEventListener("shopify:section:unload", () => {
  initScrollState();
});

/**
 * Scroll state — toggles body.is-scrolled once the user has scrolled past the
 * header-top height. Used by:
 *   - CSS transparent header  (:has([data-hero-first]):not(.is-scrolled))
 *   - CSS --header-current-height  (html:has(body.is-scrolled) switches to --header-main-height)
 * Runs on every page regardless of whether a hero is present.
 */
let _scrollStateHandler = null;

function initScrollState() {
  if (_scrollStateHandler) {
    window.removeEventListener("scroll", _scrollStateHandler, {
      passive: true,
    });
    _scrollStateHandler = null;
  }

  const headerTopHeight =
    parseInt(
      getComputedStyle(document.documentElement).getPropertyValue(
        "--header-top-height",
      ),
    ) || 0;

  const SCROLL_THRESHOLD = headerTopHeight > 0 ? headerTopHeight : 50;

  function update() {
    document.body.classList.toggle(
      "is-scrolled",
      window.scrollY >= SCROLL_THRESHOLD,
    );
  }

  update();

  _scrollStateHandler = update;
  window.addEventListener("scroll", _scrollStateHandler, { passive: true });
}

initScrollState();

/**
 * Removes aria-controls from a Swiper instance's nav buttons.
 * Swiper's a11y module points aria-controls to its internal swiper-wrapper id
 * that lives in the component's shadow DOM, which fails Lighthouse's "valid
 * value" check (target id is not reachable). Our buttons already carry a
 * proper aria-label, so the attribute is redundant.
 */
function stripSwiperAriaControls(swiper) {
  const els = [swiper?.navigation?.prevEl, swiper?.navigation?.nextEl].flat();
  els.forEach((el) => el?.removeAttribute?.("aria-controls"));
}

/**
 * Initializes a product-card swiper with the shared peek-carousel config used by
 * the predictive-search results and the in-article featured products.
 */
function initProductSwiper(swiper) {
  if (!swiper || swiper.swiper) return;

  Object.assign(swiper, {
    slidesPerView: 1.5,
    spaceBetween: 12,
    grabCursor: true,
    breakpoints: {
      420: { slidesPerView: 2.2 },
      680: { slidesPerView: 3.2 },
      1180: { slidesPerView: 4.2 },
      1400: { slidesPerView: 5.2, spaceBetween: 24 },
    },
    on: {
      init: (instance) => {
        instance.el.style.overflow = "visible";
      },
    },
  });

  swiper.initialize();
}

/**
 * Initializes a product-card swiper with the looping, arrow-navigated peek config
 * shared by product-recommendations and the in-article featured products.
 *
 * @param {HTMLElement} swiper - The swiper-container element.
 * @param {object} opts
 * @param {HTMLElement} [opts.container] - Element whose inline padding sets the slide offsets.
 * @param {string} opts.navPrev - Selector for the previous-slide button.
 * @param {string} opts.navNext - Selector for the next-slide button.
 * @param {object} opts.breakpoints - Swiper responsive breakpoints.
 */
function initRecommendationsSwiper(
  swiper,
  { container, navPrev, navNext, breakpoints },
) {
  if (!swiper || swiper.swiper) return;

  const getOffset = () =>
    container ? parseFloat(getComputedStyle(container).paddingLeft) : 0;

  Object.assign(swiper, {
    slidesPerView: 1.5,
    spaceBetween: 16,
    slidesOffsetBefore: getOffset(),
    slidesOffsetAfter: getOffset(),
    loop: true,
    loopAdditionalSlides: 2,
    watchOverflow: true,
    navigation: { nextEl: navNext, prevEl: navPrev },
    breakpoints,
    on: {
      init: (instance) => {
        instance.el.style.overflow = "visible";
        if (window.innerWidth < 992) {
          instance.params.centeredSlides = false;
          instance.update();
        }
      },
      afterInit: stripSwiperAriaControls,
      resize: (instance) => {
        const offset = getOffset();
        instance.params.slidesOffsetBefore = offset;
        instance.params.slidesOffsetAfter = offset;
      },
    },
  });

  swiper.initialize();
}

/**
 * Debounce utility
 */
function debounce(fn, delay) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

// The `t` filter HTML-escapes its output, so a translation that travels through
// a JSON attribute (rather than being parsed as HTML) arrives still escaped.
function decodeEntities(text) {
  const el = document.createElement("textarea");
  el.innerHTML = text;
  return el.value;
}

/**
 * Parses a Shopify Section Rendering API HTML string and returns
 * the element matching `selector` (defaults to `.shopify-section` wrapper).
 * Falls back to the raw string if no wrapper is found.
 *
 * @param {string} html  Raw HTML from the sections API
 * @param {string} [selector=".shopify-section"]
 * @returns {{ element: Element|null, rawHtml: string }}
 */
function parseSectionHtml(html, selector = ".shopify-section") {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.querySelector(selector);
}

class DrawerElement extends HTMLElement {
  constructor() {
    super();

    this.triggers = [
      ...this.querySelectorAll("[drawer-trigger]"),
      ...document.querySelectorAll(`[external-drawer-trigger="${this.id}"]`),
    ];

    this.drawer = this.querySelector("dialog");
    this._handleDrawerClick = this._handleDrawerClick.bind(this);
  }

  connectedCallback() {
    this.triggers.forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggle();
      });
    });

    if (this.drawer) {
      this.drawer.addEventListener("click", this._handleDrawerClick);
    }
  }

  _handleDrawerClick(event) {
    const closer = event.target.closest("[drawer-close]");
    if (closer) {
      event.stopPropagation();
      event.preventDefault();
      if (closer.closest("dialog") === this.drawer) this.close();
    } else if (event.target === this.drawer) {
      this.close();
    }
  }

  toggle() {
    if (this.drawer && this.drawer.open) this.close();
    else this.open();
  }

  close() {
    if (this.drawer) this.drawer.close();
    this._syncTriggersExpanded(false);
  }

  open() {
    if (this.drawer) this.drawer.showModal();
    this._syncTriggersExpanded(true);
  }

  _syncTriggersExpanded(expanded) {
    this.triggers.forEach((t) => {
      if (t.hasAttribute("aria-expanded")) {
        t.setAttribute("aria-expanded", expanded ? "true" : "false");
      }
    });
  }
}
if (!customElements.get("drawer-element"))
  customElements.define("drawer-element", DrawerElement);

class ReadMoreTrigger extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", (e) => {
      const btn = e.target.closest("button, a");
      if (!btn || !this.contains(btn)) return;
      e.preventDefault();
      this._activate();
    });
  }

  _activate() {
    const targetId = this.dataset.target;
    if (!targetId) return;
    const target = document.getElementById(targetId);
    if (!target) return;

    if (target instanceof HTMLDetailsElement) {
      target.open = true;
      // Two RAFs: first lets <details> reflow with new content, second lets
      // the sticky parent re-measure before we scroll.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          target.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      });
    } else if (typeof target.open === "function") {
      target.open();
    }
  }
}
if (!customElements.get("read-more-trigger"))
  customElements.define("read-more-trigger", ReadMoreTrigger);

class ExpandableText extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector("[data-expandable-content]");
    this.toggle = this.querySelector("[data-expandable-toggle]");
    this.labelMore = this.querySelector("[data-expandable-label-more]");
    this.labelLess = this.querySelector("[data-expandable-label-less]");
    if (!this.content || !this.toggle) return;

    this._sync = this._sync.bind(this);
    this._sync();

    this.toggle.addEventListener("click", () => {
      // Toggle first so the line-clamp is gone before measuring — otherwise
      // scrollHeight reports the clamped height and the expanded text stays cut.
      const expanded = this.toggleAttribute("expanded");
      this.style.setProperty("--expand-max", `${this.content.scrollHeight}px`);
      this.toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
      this.labelMore?.toggleAttribute("hidden", expanded);
      this.labelLess?.toggleAttribute("hidden", !expanded);
    });

    this._ro = new ResizeObserver(this._sync);
    this._ro.observe(this.content);
  }

  disconnectedCallback() {
    this._ro?.disconnect();
  }

  _sync() {
    if (this.hasAttribute("expanded")) return;
    const overflowing =
      this.content.scrollHeight - this.content.clientHeight > 1;
    this.toggle.toggleAttribute("hidden", !overflowing);
  }
}
if (!customElements.get("expandable-text"))
  customElements.define("expandable-text", ExpandableText);

class CountdownTimer extends HTMLElement {
  connectedCallback() {
    this.endAt = parseInt(this.dataset.end, 10);
    this.startAt = this.dataset.start ? parseInt(this.dataset.start, 10) : null;
    if (!this.endAt) return;

    this.nums = {
      days: this.querySelector("[data-days]"),
      hours: this.querySelector("[data-hours]"),
      minutes: this.querySelector("[data-minutes]"),
      seconds: this.querySelector("[data-seconds]"),
    };

    this._tick = this._tick.bind(this);
    this._tick();
    if (!this._done) this._timer = setInterval(this._tick, 1000);
  }

  disconnectedCallback() {
    clearInterval(this._timer);
  }

  _tick() {
    const now = Math.floor(Date.now() / 1000);
    if (this.startAt && now < this.startAt) {
      this.hidden = true;
      return;
    }
    const remaining = this.endAt - now;
    if (remaining <= 0) {
      this._expire();
      return;
    }
    this.hidden = false;
    this._set(this.nums.days, Math.floor(remaining / 86400));
    this._set(this.nums.hours, Math.floor((remaining % 86400) / 3600));
    this._set(this.nums.minutes, Math.floor((remaining % 3600) / 60));
    this._set(this.nums.seconds, remaining % 60);
  }

  _set(el, value) {
    if (el) el.textContent = String(value).padStart(2, "0");
  }

  _expire() {
    this._done = true;
    clearInterval(this._timer);
    // Remove the whole host (swiper slide / discount bar / card / cart bar), not
    // just the timer, so nothing empty is left behind. `hidden` alone can't hide
    // it: the `.countdown` display rule overrides the attribute.
    this.dispatchEvent(new CustomEvent("countdown:expired", { bubbles: true }));
    const host =
      this.closest(
        ".announcement, .discount-bar, .discount-card--countdown, .cart-drawer__countdown",
      ) || this;
    host.remove();
  }
}
if (!customElements.get("countdown-timer"))
  customElements.define("countdown-timer", CountdownTimer);

class ColorSwatches extends HTMLElement {
  constructor() {
    super();
    this._handleColorSwitch = this._handleColorSwitch.bind(this);
  }

  connectedCallback() {
    this._toggle = this.querySelector(".product-card__color-swatches__more");
    this._toggle?.addEventListener("click", (event) => {
      event.preventDefault();
      this.setAttribute("expanded", "");
      this._toggle.setAttribute("aria-expanded", "true");
    });

    this._card = this.closest(".product-card");
    this._link = this._card?.querySelector(".product-card__link");
    this._blur = this._card?.querySelector(".product-card__swatch-preview__blur");
    this._full = this._card?.querySelector(".product-card__swatch-preview__full");
    this._allSwatches = this.querySelectorAll(".color-swatch");
    this._swatches = this.querySelectorAll(".color-swatch[data-swap-src]");

    window.addEventListener("color-switcher:change", this._handleColorSwitch);

    if (!this._full || !this._swatches.length) return;

    this._pointerType = "mouse";
    this._full.addEventListener("load", () => {
      this._full.classList.add("is-loaded");
    });

    this._swatches.forEach((swatch) => {
      swatch.addEventListener("pointerdown", (e) => {
        this._pointerType = e.pointerType || "mouse";
      });
      swatch.addEventListener("pointerenter", (e) => {
        if ((e.pointerType || "mouse") !== "touch") this._swap(swatch);
      });
      swatch.addEventListener("focus", () => this._swap(swatch));
      swatch.addEventListener("click", (e) => {
        // Desktop: let the swatch navigate to the colour's PDP. Touch: no
        // hover, so a tap picks the colour on the card instead.
        if (this._pointerType === "touch") {
          e.preventDefault();
          this._select(swatch);
        }
      });
    });

    this.addEventListener("mouseleave", () => this._restore());
    this.addEventListener("focusout", (e) => {
      if (!this.contains(e.relatedTarget)) this._restore();
    });
  }

  _swap(swatch) {
    const src = swatch.getAttribute("data-swap-src");
    if (this._full && src) {
      if (this._full.getAttribute("src") !== src) {
        this._full.classList.remove("is-loaded");
        this._blur?.setAttribute("src", swatch.getAttribute("data-swap-blur") || "");
        this._full.setAttribute("src", src);
        const srcset = swatch.getAttribute("data-swap-srcset");
        if (srcset) this._full.setAttribute("srcset", srcset);
        else this._full.removeAttribute("srcset");
      }
      // Already-cached image fires no `load` — reveal it right away.
      if (this._full.complete && this._full.naturalWidth) {
        this._full.classList.add("is-loaded");
      }
      this._card.classList.add("is-swatch-preview");
    }
  }

  // Hover only previews a colour's photo; picking one turns the whole card into
  // that product, down to its link, badges, title and price.
  _select(swatch) {
    const current = swatch.classList.contains("color-swatch--current");
    this._selected = current ? null : swatch;
    this._allSwatches.forEach((s) =>
      s.classList.toggle("color-swatch--active", s === this._selected),
    );
    if (current) this._card.classList.remove("is-swatch-preview");
    else this._swap(swatch);

    if (this._link) this._link.href = swatch.getAttribute("href");

    // Only swatches loaded after the page carry their product's details.
    const details = swatch.querySelector("template")?.content;
    if (!details) return;
    for (const slot of [".product-card__badges", ".product-card__content"]) {
      const fresh = details.querySelector(slot);
      const target = this._card.querySelector(slot);
      if (fresh && target) target.replaceChildren(...fresh.cloneNode(true).childNodes);
    }
  }

  _restore() {
    // A picked colour is the card's new baseline, so hover has to fall back to
    // it rather than to the product the card was rendered for.
    if (this._selected) {
      this._swap(this._selected);
      return;
    }
    this._card.classList.remove("is-swatch-preview");
  }

  _handleColorSwitch(event) {
    if (!this._card) return;
    const handle = event.detail?.handle;

    // No colour picked puts the card back on the product it was rendered for.
    const swatch = handle
      ? this.querySelector(`.color-swatch[data-color-handle="${CSS.escape(handle)}"]`)
      : this.querySelector(".color-swatch--current");
    // A model this colour isn't made in has nothing to show for it.
    this._card.classList.toggle("product-card--hidden", !swatch);
    if (swatch) this._select(swatch);
  }

  disconnectedCallback() {
    window.removeEventListener("color-switcher:change", this._handleColorSwitch);
  }
}
if (!customElements.get("color-swatches"))
  customElements.define("color-swatches", ColorSwatches);

// Tallies sit outside the grid — in the hero, the toolbar and the filter
// drawer — so each keeps itself in step with the cards rather than being
// written to by whatever moved them. Defined ahead of <color-switcher> so a
// colour deep-linked in the URL is applied with these already listening.
class ProductCount extends HTMLElement {
  constructor() {
    super();
    this._handleColorSwitch = this._handleColorSwitch.bind(this);
  }

  connectedCallback() {
    this.grid = document.getElementById(this.dataset.gridId || "CollectionGrid");
    if (!this.grid) return;

    if (this.dataset.countForms) this._forms = JSON.parse(this.dataset.countForms);
    window.addEventListener("color-switcher:change", this._handleColorSwitch);
  }

  disconnectedCallback() {
    window.removeEventListener("color-switcher:change", this._handleColorSwitch);
  }

  _handleColorSwitch() {
    // Only one page of a paginated grid is in the DOM, so its cards can't add
    // up to the listing's tally.
    if (this.grid.querySelector(".pagination")) return;

    // Every card answers the same event, so the tally is only right once they
    // all have — hence the wait for the end of the dispatch.
    queueMicrotask(() => this._render(
      this.grid.querySelectorAll(".product-card:not(.product-card--hidden)").length,
    ));
  }

  _render(count) {
    this.textContent = this._forms ? this._plural(count) : count;
  }

  // Liquid ships one rendering per sample count; the one to reuse is whichever
  // sample falls in the same plural category as the new number.
  _plural(count) {
    const rules = new Intl.PluralRules(document.documentElement.lang || "en");
    const category = rules.select(count);
    const samples = Object.keys(this._forms);
    const sample =
      samples.find((n) => rules.select(Number(n)) === category) ?? samples.at(-1);
    return this._forms[sample].replace(sample, count);
  }
}
if (!customElements.get("product-count"))
  customElements.define("product-count", ProductCount);

class ColorSwitcher extends HTMLElement {
  static PARAM = "color";

  constructor() {
    super();
    this._handleClick = this._handleClick.bind(this);
    this._handlePopState = this._handlePopState.bind(this);
    this._showTooltip = this._showTooltip.bind(this);
    this._hideTooltip = this._hideTooltip.bind(this);
    this._handleKeydown = this._handleKeydown.bind(this);
    this._updateEnd = this._updateEnd.bind(this);
  }

  connectedCallback() {
    this.grid = document.getElementById(this.dataset.gridId || "CollectionGrid");
    this.track = this.querySelector(".color-switcher__track");
    if (!this.grid || !this.track) return;

    this._tooltip = this.querySelector(".color-switcher__tooltip");
    if (this._tooltip) {
      this.track.addEventListener("pointerover", this._showTooltip);
      this.track.addEventListener("focusin", this._showTooltip);
      this.track.addEventListener("pointerout", this._hideTooltip);
      this.track.addEventListener("focusout", this._hideTooltip);
      this.track.addEventListener("scroll", this._hideTooltip, { passive: true });
      this.addEventListener("keydown", this._handleKeydown);
    }

    // In the drawer the row lives inside a filter accordion, which is what has
    // to appear and disappear with it.
    this._host = this.closest("details.color-switcher__details") || this;

    this._build();
    this.addEventListener("click", this._handleClick);
    window.addEventListener("popstate", this._handlePopState);

    this.track.addEventListener("scroll", this._updateEnd, { passive: true });
    this._resizeObserver = new ResizeObserver(this._updateEnd);
    this._resizeObserver.observe(this.track);

    // Sorting and filtering re-render the grid and swatches arrive after the
    // page, so the palette and the pressed chip follow the swatches. Only their
    // arrival or departure counts: cards rewrite their own details on every
    // colour change, which would otherwise rebuild — and re-apply — forever.
    const hasSwatches = (node) =>
      node.nodeType === Node.ELEMENT_NODE &&
      (node.matches("color-swatches") || node.querySelector("color-swatches"));
    this._observer = new MutationObserver((records) => {
      if (
        records.some((record) =>
          [...record.addedNodes, ...record.removedNodes].some(hasSwatches),
        )
      ) {
        this._build();
      }
    });
    this._observer.observe(this.grid, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.removeEventListener("click", this._handleClick);
    window.removeEventListener("popstate", this._handlePopState);
    this._observer?.disconnect();
    this._resizeObserver?.disconnect();
  }

  // The cards already render every colour of their group as a swatch, so the
  // row is mirrored off them: it can never drift from the grid, and the page
  // pays nothing for a second pass over the metaobjects.
  _build() {
    // The palette is mirrored off the swatches, so it waits for them.
    if (this.grid.querySelector("[data-swatches-for]")) return;

    // A listing that shows each model in its own colour has no base colour to
    // rest on, so its resting state is no colour picked at all.
    const current = new Set(
      [...this.grid.querySelectorAll(".color-swatch--current")].map(
        (swatch) => swatch.dataset.colorHandle,
      ),
    );
    this._base = current.size === 1 ? [...current][0] : "";

    const colors = new Map();
    for (const swatch of this.grid.querySelectorAll(
      ".color-swatch[data-color-handle]",
    )) {
      const handle = swatch.dataset.colorHandle;
      if (handle && !colors.has(handle)) colors.set(handle, swatch);
    }

    if (colors.size < 2) {
      this.track.replaceChildren();
      this._host.hidden = true;
      return;
    }

    const chips = document.createDocumentFragment();
    for (const [handle, swatch] of colors) chips.append(this._chip(handle, swatch));
    this.track.replaceChildren(chips);
    this._host.hidden = false;
    this._updateEnd();

    this._colors = colors;
    this._apply(this._fromUrl());
  }

  _chip(handle, swatch) {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = "color-chip";
    if (handle === "custom-ral-ncs") chip.classList.add("color-chip--custom");
    chip.dataset.colorHandle = handle;
    chip.setAttribute("aria-pressed", "false");

    const dot = document.createElement("span");
    dot.className = "color-chip__swatch";
    const color = swatch.style.getPropertyValue("--swatch-color");
    if (color) dot.style.setProperty("--swatch-color", color);
    const image = swatch.querySelector("img");
    if (image) dot.append(image.cloneNode());

    const name = document.createElement("span");
    name.className = "color-chip__name";
    name.textContent = swatch.getAttribute("title") || handle;

    chip.append(dot, name);
    return chip;
  }

  _handleClick(event) {
    const chip = event.target.closest(".color-chip");
    if (!chip || !this.track.contains(chip)) return;
    this._select(chip.dataset.colorHandle);
  }

  _handlePopState() {
    this._apply(this._fromUrl());
  }

  // The base colour is the bare collection URL, so the default state stays on
  // the address the canonical tag points at.
  _fromUrl() {
    const handle = new URLSearchParams(location.search).get(ColorSwitcher.PARAM);
    return handle && this._colors.has(handle) ? handle : this._base;
  }

  _select(handle) {
    if (handle === this._active) {
      if (this._base) return;
      handle = this._base;
    }
    this._apply(handle);

    const url = new URL(location.href);
    if (handle === this._base) url.searchParams.delete(ColorSwitcher.PARAM);
    else url.searchParams.set(ColorSwitcher.PARAM, handle);
    history.pushState({ [ColorSwitcher.PARAM]: handle }, "", url);
  }

  // Idempotent: _build() replays it over a freshly rendered grid, so it can't
  // lean on the handle that was active before.
  _apply(handle) {
    this._mark(handle);

    // Single-colour models carry no swatches, so no card component speaks for
    // them — they belong in the grid only in the colour they are made in.
    for (const card of this.grid.querySelectorAll(
      ".product-card:not(:has(color-swatches))",
    )) {
      card.classList.toggle("product-card--hidden", handle !== this._base);
    }

    window.dispatchEvent(
      new CustomEvent("color-switcher:change", { detail: { handle } }),
    );
  }

  _mark(handle) {
    this._active = handle;
    for (const chip of this.track.children) {
      chip.setAttribute(
        "aria-pressed",
        String(chip.dataset.colorHandle === handle),
      );
    }
  }

  // The track scrolls sideways and would clip anything poking out of it, so the
  // tooltip lives on the switcher and is placed under the hovered swatch.
  _showTooltip(event) {
    const chip = event.target.closest(".color-chip:not(.color-chip--skeleton)");
    if (!chip) return;
    const box = this.getBoundingClientRect();
    const rect = chip.getBoundingClientRect();
    this._tooltip.textContent = chip.querySelector(".color-chip__name").textContent;
    this._tooltip.style.left = `${rect.left - box.left + rect.width / 2}px`;
    this._tooltip.style.top = `${rect.bottom - box.top}px`;
    this._tooltip.hidden = false;
  }

  // The fade on the trailing edge hints at more colours, so it goes once the
  // row is scrolled to its end or has nothing to scroll.
  _updateEnd() {
    const { scrollLeft, clientWidth, scrollWidth } = this.track;
    this.track.toggleAttribute("data-at-end", scrollLeft + clientWidth >= scrollWidth - 1);
  }

  _hideTooltip() {
    this._tooltip.hidden = true;
  }

  _handleKeydown(event) {
    if (event.key === "Escape") this._hideTooltip();
  }
}

if (!customElements.get("color-switcher"))
  customElements.define("color-switcher", ColorSwitcher);

class CartDrawer extends DrawerElement {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    const contents = this.drawer?.querySelector(".drawer__contents");
    if (contents) {
      this._updateScrollShadows = () => {
        this.drawer.classList.toggle("can-scroll-up", contents.scrollTop > 0);
        this.drawer.classList.toggle(
          "can-scroll-down",
          Math.ceil(contents.scrollTop + contents.clientHeight) <
            contents.scrollHeight,
        );
      };
      contents.addEventListener("scroll", this._updateScrollShadows, {
        passive: true,
      });
      new ResizeObserver(this._updateScrollShadows).observe(contents);
    }

    window.addEventListener("cart::update", (event) => {
      const drawerHtml =
        event.detail.cartContentsDrawer ?? event.detail.cartContents;
      if (drawerHtml) {
        this.renderContents(drawerHtml);
      }
      // Only open on add-to-cart, and only when cart drawer mode is active
      if (
        event.detail.cartMessage === "added" &&
        document.body.dataset.addToCartAction !== "added_to_cart"
      ) {
        this.open();
      }
    });
  }

  open() {
    super.open();
    this._updateScrollShadows?.();
  }

  renderContents(html) {
    if (!this.drawer || !html) return;

    const drawerContents =
      this.drawer.querySelector(".drawer__contents") || this.drawer;

    const section = parseSectionHtml(html);
    drawerContents.innerHTML = section ? section.innerHTML : html;

    // Fade-in: start new cart-contents at locked opacity, then animate to full
    const cartContents = drawerContents.querySelector("cart-contents");
    if (cartContents) {
      cartContents.setAttribute("data-locked", "");
      requestAnimationFrame(() =>
        requestAnimationFrame(() =>
          cartContents.removeAttribute("data-locked"),
        ),
      );
    }

    this._updateScrollShadows?.();
  }
}
if (!customElements.get("cart-drawer"))
  customElements.define("cart-drawer", CartDrawer);

class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.submit = this.querySelector('button[type="submit"]');
    this.form = this.querySelector("form") || this.closest("form");
    this.errorContainer = this.querySelector(".product-form__error-container");
  }

  connectedCallback() {
    this.form?.addEventListener("submit", (e) => {
      e.preventDefault();
      this.setLoading(true);
      this.addToCart();
    });
  }

  setLoading(state) {
    this.submit.toggleAttribute("data-loading", state);
  }

  addToCart() {
    const pendingClearance = document.querySelector(
      "clearance-option[data-clearance-pending]",
    );
    if (pendingClearance) {
      pendingClearance.dispatchEvent(new CustomEvent("clearance:required"));
      this.showError(
        pendingClearance.querySelector("[data-clearance-error]")?.dataset
          .clearanceRequiredMsg || "",
      );
      this.setLoading(false);
      return;
    }

    const pendingColor = document.querySelector(
      "custom-color-input[data-custom-color-pending]",
    );
    if (pendingColor) {
      pendingColor.dispatchEvent(new CustomEvent("custom-color:required"));
      this.showError(
        pendingColor.querySelector("[data-custom-color-error]")?.dataset
          .requiredMsg || "",
      );
      this.setLoading(false);
      return;
    }

    const formData = new FormData(this.form);

    // FormData doesn't reliably collect inputs outside the <form> element
    // even when they carry a matching form= attribute — collect them manually.
    if (this.form.id) {
      document.querySelectorAll(`[form="${this.form.id}"]`).forEach((input) => {
        if (!input.disabled && input.name && input.value !== "") {
          formData.set(input.name, input.value);
        }
      });
    }

    if (!formData.get("id")) {
      console.error("Product ID not found in form");
      this.showError("Could not identify product. Please try again.");
      this.setLoading(false);
      return;
    }

    const body = JSON.stringify({
      items: [
        {
          id: formData.get("id"),
          quantity: parseInt(formData.get("quantity") || 1, 10),
          ...this.getFormProperties(formData),
        },
      ],
      sections:
        document.body.dataset.addToCartAction === "added_to_cart"
          ? ["cart-contents", "cart-contents-drawer", "added-to-cart-contents"]
          : ["cart-contents", "cart-contents-drawer"],
    });

    fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: body,
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((err) => {
            throw new Error(
              err.description ||
                err.message ||
                `Cart error: ${response.status}`,
            );
          });
        }
        return response.json();
      })
      .then((data) => {
        window.dispatchEvent(
          new CustomEvent("cart::update", {
            detail: {
              cartMessage: "added",
              addedItem: data,
              cartContents: data.sections?.["cart-contents"],
              cartContentsDrawer: data.sections?.["cart-contents-drawer"],
              addedToCartContents: data.sections?.["added-to-cart-contents"],
            },
          }),
        );
      })
      .catch((error) => {
        console.error("Error adding to cart:", error);
        this.showError(
          error.message || "Could not add to cart. Please try again.",
        );
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  getFormProperties(formData) {
    const properties = {};
    let hasProperties = false;

    for (const [key, value] of formData.entries()) {
      if (key.startsWith("properties[") && key.endsWith("]")) {
        const propertyName = key.slice(11, -1);
        properties[propertyName] = value;
        hasProperties = true;
      }
    }

    return hasProperties ? { properties } : {};
  }

  showError(message) {
    if (this.errorContainer) {
      this.errorContainer.textContent = message;
      setTimeout(() => {
        this.errorContainer.textContent = "";
      }, 3000);
    }
  }
}
if (!customElements.get("product-form"))
  customElements.define("product-form", ProductForm);

class VideoElement extends HTMLElement {
  constructor() {
    super();

    this.desktopVideo = this.querySelector(".video-player--desktop");
    this.mobileVideo = this.querySelector(".video-player--mobile");
    this.playButton = this.querySelector("[data-play-button]");
    this.statusElement = this.querySelector("[data-video-status]");

    this.autoplay = this.dataset.autoplay === "true";
    this.loop = this.dataset.loop === "true";
    this.muted = this.dataset.muted === "true";
    this.hasMobileVideo = this.dataset.hasMobile === "true";

    this.currentVideo = null;
    this.isMobile = false;

    this._handlePlayButtonClick = this._handlePlayButtonClick.bind(this);
    this._handleKeydown = this._handleKeydown.bind(this);
    this._handleVideoPlay = this._handleVideoPlay.bind(this);
    this._handleVideoPause = this._handleVideoPause.bind(this);
    this._handleVideoEnded = this._handleVideoEnded.bind(this);
    this._handleResize = this._handleResize.bind(this);
  }

  connectedCallback() {
    this.checkViewport();
    this.setupEventListeners();

    if (this.autoplay && this.currentVideo) {
      this.currentVideo.play().catch(() => {
        this.updateState("paused");
      });
    } else {
      this.updateState("paused");
    }
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  checkViewport() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 768;

    const shouldUseMobile = this.isMobile && this.hasMobileVideo;
    const newVideo = shouldUseMobile ? this.mobileVideo : this.desktopVideo;

    if (newVideo !== this.currentVideo) {
      const wasPlaying = this.currentVideo && !this.currentVideo.paused;
      const currentTime = this.currentVideo ? this.currentVideo.currentTime : 0;

      this.currentVideo = newVideo;

      if (wasPlaying && this.currentVideo) {
        this.currentVideo.currentTime = currentTime;
        this.currentVideo.play();
      }
    }
  }

  setupEventListeners() {
    if (this.playButton) {
      this.playButton.addEventListener("click", this._handlePlayButtonClick);
      this.playButton.addEventListener("keydown", this._handleKeydown);
    }

    if (this.currentVideo) {
      this.currentVideo.addEventListener("play", this._handleVideoPlay);
      this.currentVideo.addEventListener("pause", this._handleVideoPause);
      this.currentVideo.addEventListener("ended", this._handleVideoEnded);
    }

    window.addEventListener("resize", this._handleResize);
  }

  removeEventListeners() {
    if (this.playButton) {
      this.playButton.removeEventListener("click", this._handlePlayButtonClick);
      this.playButton.removeEventListener("keydown", this._handleKeydown);
    }

    if (this.desktopVideo) {
      this.desktopVideo.removeEventListener("play", this._handleVideoPlay);
      this.desktopVideo.removeEventListener("pause", this._handleVideoPause);
      this.desktopVideo.removeEventListener("ended", this._handleVideoEnded);
    }

    if (this.mobileVideo) {
      this.mobileVideo.removeEventListener("play", this._handleVideoPlay);
      this.mobileVideo.removeEventListener("pause", this._handleVideoPause);
      this.mobileVideo.removeEventListener("ended", this._handleVideoEnded);
    }

    window.removeEventListener("resize", this._handleResize);
  }

  _handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.checkViewport();
    }, 150);
  }

  _handlePlayButtonClick(event) {
    event.preventDefault();
    this.togglePlay();
  }

  _handleKeydown(event) {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      this.togglePlay();
    }
  }

  _handleVideoPlay() {
    this.updateState("playing");
    this.announceToScreenReader("Video playing");
  }

  _handleVideoPause() {
    this.updateState("paused");
    this.announceToScreenReader("Video paused");
  }

  _handleVideoEnded() {
    this.updateState("paused");
    this.announceToScreenReader("Video ended");
  }

  togglePlay() {
    if (!this.currentVideo) return;

    if (this.currentVideo.paused) {
      this.currentVideo.play().catch((error) => {
        console.error("Error playing video:", error);
        this.announceToScreenReader("Unable to play video");
      });
    } else {
      this.currentVideo.pause();
    }
  }

  updateState(state) {
    this.setAttribute("data-state", state);

    if (this.playButton) {
      const label =
        state === "playing"
          ? this.playButton.dataset.labelStop || "Stop video"
          : this.playButton.dataset.labelPlay || "Play video";
      this.playButton.setAttribute("aria-label", label);
    }
  }

  announceToScreenReader(message) {
    if (this.statusElement) {
      this.statusElement.textContent = message;
    }
  }
}
if (!customElements.get("video-element"))
  customElements.define("video-element", VideoElement);

/**
 * QtyInput
 *
 * Accessible quantity stepper with decrement / increment buttons.
 * Fires a "qty-input:change" CustomEvent (bubbles, composed) on every
 * committed value change so parent components can react independently.
 *
 * Event detail: { value: Number, previousValue: Number, input: HTMLInputElement }
 *
 * Usage in Liquid: {%- render 'qty-input', value: 1, min: 1, product: product.title -%}
 */
class QtyInput extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector(".qty-input__input");
    this.decrementBtn = this.querySelector(".qty-input__btn--decrement");
    this.incrementBtn = this.querySelector(".qty-input__btn--increment");

    this._handleDecrement = this._handleDecrement.bind(this);
    this._handleIncrement = this._handleIncrement.bind(this);
    this._handleInputChange = this._handleInputChange.bind(this);
    this._handleKeydown = this._handleKeydown.bind(this);
  }

  connectedCallback() {
    if (!this.input) return;

    // Seed committed value from the current input so button state is correct
    this._committedValue = parseInt(this.input.value, 10) || this.min;

    this.decrementBtn?.addEventListener("click", this._handleDecrement);
    this.incrementBtn?.addEventListener("click", this._handleIncrement);
    this.input.addEventListener("change", this._handleInputChange);
    this.input.addEventListener("keydown", this._handleKeydown);

    // Reflect initial boundary state
    this._updateButtonStates();
  }

  disconnectedCallback() {
    this.decrementBtn?.removeEventListener("click", this._handleDecrement);
    this.incrementBtn?.removeEventListener("click", this._handleIncrement);
    this.input?.removeEventListener("change", this._handleInputChange);
    this.input?.removeEventListener("keydown", this._handleKeydown);
  }

  // ─── Public API ────────────────────────────────────────────────────────────

  get value() {
    // Read from the last committed (dispatched) value, not the raw input.
    // This ensures manual typing doesn't silently shift the baseline before
    // _setValue compares old vs new.
    return this._committedValue ?? parseInt(this.input?.value ?? 1, 10);
  }

  set value(newValue) {
    this._setValue(newValue);
  }

  get min() {
    return parseInt(this.input?.min ?? 1, 10);
  }

  get max() {
    const raw = this.input?.max;
    return raw !== "" && raw != null ? parseInt(raw, 10) : Infinity;
  }

  // ─── Private helpers ───────────────────────────────────────────────────────

  _setValue(newValue) {
    if (!this.input) return;

    const clamped = Math.min(Math.max(newValue, this.min), this.max);
    const previous = this._committedValue ?? this.min;

    this.input.value = clamped;
    this._committedValue = clamped;
    this._updateButtonStates();

    if (clamped !== previous) {
      this._dispatch(clamped, previous);
    }
  }

  _updateButtonStates() {
    if (!this.input) return;

    const currentValue = this.value;

    if (this.decrementBtn) {
      const atMin = currentValue <= this.min;
      this.decrementBtn.setAttribute("aria-disabled", atMin);
      // Keep buttons focusable but communicate state via aria
      this.decrementBtn.disabled = atMin;
    }

    if (this.incrementBtn) {
      const atMax = currentValue >= this.max;
      this.incrementBtn.setAttribute("aria-disabled", atMax);
      this.incrementBtn.disabled = atMax;
    }
  }

  _dispatch(value, previousValue) {
    this.dispatchEvent(
      new CustomEvent("qty-input:change", {
        bubbles: true,
        composed: true,
        detail: { value, previousValue, input: this.input },
      }),
    );
  }

  // ─── Event handlers ────────────────────────────────────────────────────────

  _handleDecrement() {
    this._setValue(this.value - 1);
  }

  _handleIncrement() {
    this._setValue(this.value + 1);
  }

  _handleInputChange() {
    // Fired on blur / Enter; sanitise and commit the raw typed value
    const parsed = parseInt(this.input.value, 10);
    this._setValue(isNaN(parsed) ? this.min : parsed);
  }

  _handleKeydown(event) {
    if (event.key === "ArrowUp") {
      event.preventDefault();
      this._setValue(this.value + 1);
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      this._setValue(this.value - 1);
    }
  }
}
if (!customElements.get("qty-input"))
  customElements.define("qty-input", QtyInput);

/**
 * <volume-discount> — volume discount status above the add-to-cart button.
 *
 * With `data-qty-input` (the id of a qty-input's <input>), the status counts
 * that quantity as added to the cart, and follows both the input and the cart:
 * `cart::update` carries the re-rendered <cart-contents>, whose
 * `data-volume-discount-quantity` holds the eligible units now in the cart.
 * Without it the server-rendered status stays as is.
 */
class VolumeDiscount extends HTMLElement {
  constructor() {
    super();
    this._render = this._render.bind(this);
    this._handleCartUpdate = this._handleCartUpdate.bind(this);
  }

  connectedCallback() {
    const input = document.getElementById(this.dataset.qtyInput ?? "");
    this.qtyInput = input?.closest("qty-input");
    if (!this.qtyInput) return;

    this.message = this.querySelector("[data-volume-discount-message]");
    this.tiers = this.dataset.tiers
      .split(",")
      .map((tier) => tier.split(":").map(Number));
    this.cartQuantity = Number(this.dataset.cartQuantity);
    this.messages = JSON.parse(this.dataset.messages, (key, value) =>
      typeof value === "string" ? decodeEntities(value) : value,
    );
    this.pluralRules = new Intl.PluralRules(document.documentElement.lang);

    this.qtyInput.addEventListener("qty-input:change", this._render);
    window.addEventListener("cart::update", this._handleCartUpdate);
  }

  disconnectedCallback() {
    this.qtyInput?.removeEventListener("qty-input:change", this._render);
    window.removeEventListener("cart::update", this._handleCartUpdate);
  }

  _handleCartUpdate(event) {
    if (!event.detail.cartContents) return;
    const cart = parseSectionHtml(event.detail.cartContents, "cart-contents");
    this.cartQuantity = Number(cart?.dataset.volumeDiscountQuantity ?? 0);
    this._render();
  }

  _render() {
    const total = this.cartQuantity + this.qtyInput.value;
    const active = this.tiers.findLast(([quantity]) => quantity <= total);
    const next = this.tiers.find(([quantity]) => quantity > total);

    if (!active) {
      this.message.textContent = this._plural("start", this.tiers[0][0]);
    } else if (next) {
      this.message.textContent = this._plural("product_next", next[0] - total)
        .replace("[active]", active[1])
        .replace("[value]", next[1]);
    } else {
      this.message.textContent = this.messages.product_max.replace(
        "[value]",
        active[1],
      );
    }
  }

  _plural(group, count) {
    const forms = this.messages[group];
    const form = forms[this.pluralRules.select(count)] ?? forms.other;
    return form.replace("[count]", count);
  }
}
if (!customElements.get("volume-discount"))
  customElements.define("volume-discount", VolumeDiscount);

/**
 * CartContents
 *
 * Handles quantity changes and item removal inside the cart without a full
 * page reload. Uses the Shopify Section Rendering API to re-render itself.
 *
 * Listens for:
 *   - "qty-input:change"  (from <qty-input> children) — debounced 400 ms
 *   - click on .cart-item__remove — intercepted to use key-based AJAX removal
 *
 * Dispatches:
 *   - "cart::update" (window) — with { cartContents: html, cart: data }
 *     so CartDrawer and any other listener can update their UI
 */
class CartContents extends HTMLElement {
  constructor() {
    super();

    this._handleQtyChange = this._handleQtyChange.bind(this);
    this._handleRemoveClick = this._handleRemoveClick.bind(this);
    this._handleInvalid = this._handleInvalid.bind(this);
    this._handleConsentChange = this._handleConsentChange.bind(this);
    this._handleNoteInput = this._handleNoteInput.bind(this);

    // Debounced version of _changeCart for qty updates
    this._debouncedChange = debounce(this._changeCart.bind(this), 400);
    // Persist the order note without re-rendering (keeps focus in the field)
    this._debouncedNote = debounce(this._saveNote.bind(this), 500);
  }

  connectedCallback() {
    this.addEventListener("qty-input:change", this._handleQtyChange);
    this.addEventListener("click", this._handleRemoveClick);
    // `invalid` doesn't bubble — capture it on the way down.
    this.addEventListener("invalid", this._handleInvalid, true);
    this.addEventListener("change", this._handleConsentChange);
    this.addEventListener("input", this._handleNoteInput);
  }

  disconnectedCallback() {
    this.removeEventListener("qty-input:change", this._handleQtyChange);
    this.removeEventListener("click", this._handleRemoveClick);
    this.removeEventListener("invalid", this._handleInvalid, true);
    this.removeEventListener("change", this._handleConsentChange);
    this.removeEventListener("input", this._handleNoteInput);
  }

  // ─── Terms consent ────────────────────────────────────────────────────────

  // The checkbox is natively `required`; the browser blocks checkout on its own.
  // Here we just swap the default validation bubble for our own invalid styling.
  _handleInvalid(event) {
    const consent = event.target;
    if (!consent.matches('.cart__consent input[type="checkbox"]')) return;

    event.preventDefault();
    consent.closest(".field")?.classList.add("invalid");
    consent.setAttribute("aria-invalid", "true");
    consent.focus();
  }

  _handleConsentChange(event) {
    const consent = event.target;
    if (!consent.matches('.cart__consent input[type="checkbox"]')) return;
    if (!consent.checked) return;
    consent.closest(".field")?.classList.remove("invalid");
    consent.setAttribute("aria-invalid", "false");
  }

  // ─── Order note ─────────────────────────────────────────────────────────────

  // Persist the note server-side so it survives section re-renders (qty changes)
  // and reaches checkout. Debounced; no re-render, so the caret stays put.
  _handleNoteInput(event) {
    if (!event.target.matches(".cart__note__input")) return;
    this._debouncedNote(event.target.value);
  }

  _saveNote(note) {
    fetch(window.Shopify.routes.root + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ note }),
    }).catch((error) => console.error("Cart note update failed:", error));
  }

  // ─── Public API ────────────────────────────────────────────────────────────

  setLoading(state) {
    this.toggleAttribute("data-locked", state);
  }

  // ─── Event handlers ────────────────────────────────────────────────────────

  _handleQtyChange(event) {
    const cartItem = event.target.closest("cart-item");
    if (!cartItem) return;

    const key = cartItem.dataset.key;
    const quantity = event.detail.value;

    if (!key) return;
    this._debouncedChange({ id: key, quantity });
  }

  _handleRemoveClick(event) {
    const removeLink = event.target.closest(".cart-item__remove");
    if (!removeLink) return;

    // Prevent navigation — handle via AJAX instead
    event.preventDefault();

    const cartItem = removeLink.closest("cart-item");
    if (!cartItem) return;

    const key = cartItem.dataset.key;
    if (!key) return;

    // Remove is immediate (no debounce)
    this._changeCart({ id: key, quantity: 0 });
  }

  // ─── Core logic ────────────────────────────────────────────────────────────

  _changeCart({ id, quantity }) {
    this.setLoading(true);

    fetch(window.Shopify.routes.root + "cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        id,
        quantity,
        sections: ["cart-contents", "cart-contents-drawer"],
      }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart error: ${response.status}`);
        return response.json();
      })
      .then((data) => {
        const sectionHtml = data.sections?.["cart-contents"];
        this._renderContents(sectionHtml);

        window.dispatchEvent(
          new CustomEvent("cart::update", {
            detail: {
              cartContents: sectionHtml,
              cartContentsDrawer: data.sections?.["cart-contents-drawer"],
              cart: data,
            },
          }),
        );
      })
      .catch((error) => {
        console.error("Cart update failed:", error);
        this.setLoading(false); // unlock immediately on error
      });
  }

  _renderContents(html) {
    if (!html) return;

    const incoming = parseSectionHtml(html, "cart-contents");

    if (incoming) {
      this.innerHTML = incoming.innerHTML;
      requestAnimationFrame(() =>
        requestAnimationFrame(() => this.setLoading(false)),
      );
    } else {
      // Empty cart: replace the whole element (data-locked disappears with it)
      this.outerHTML = html;
    }
  }
}
if (!customElements.get("cart-contents"))
  customElements.define("cart-contents", CartContents);

class AddedToCartDrawer extends DrawerElement {
  connectedCallback() {
    super.connectedCallback();

    window.addEventListener("cart::update", (event) => {
      if (
        event.detail.cartMessage !== "added" ||
        document.body.dataset.addToCartAction !== "added_to_cart"
      )
        return;

      if (event.detail.addedToCartContents) {
        this.renderContents(
          event.detail.addedToCartContents,
          event.detail.addedItem,
        );
      }

      const addedItem = event.detail.addedItem;
      const productId =
        addedItem?.product_id || addedItem?.items?.[0]?.product_id;

      if (productId) {
        this.fetchRecommendations(productId);
      }
      this.open();
    });
  }

  renderContents(html, addedItem = null) {
    if (!this.drawer || !html) return;

    const drawerContents =
      this.drawer.querySelector(".drawer__contents") || this.drawer;
    const section = parseSectionHtml(html);
    const container = section || document.createElement("div");
    if (!section) container.innerHTML = html;

    if (addedItem && (addedItem.key || addedItem.items)) {
      const keys = addedItem.items
        ? addedItem.items.map((item) => item.key)
        : [addedItem.key];
      const itemsToShow = container.querySelectorAll(".added-to-cart__item");

      drawerContents.innerHTML = "";
      itemsToShow.forEach((itemEl) => {
        if (keys.includes(itemEl.dataset.key)) {
          drawerContents.appendChild(itemEl);
        }
      });
    } else {
      drawerContents.innerHTML = container.innerHTML;
    }
  }

  fetchRecommendations(productId) {
    const container = this.drawer.querySelector(
      ".added-to-cart__recommendations",
    );
    if (!container) return;

    const url = `${window.Shopify.routes.root}recommendations/products?section_id=added-to-cart-recommendations&product_id=${productId}&limit=4&intent=related`;

    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        const section = parseSectionHtml(html);
        const innerHTML = section ? section.innerHTML : html;

        if (innerHTML.trim() !== "") {
          container.innerHTML = innerHTML;
        } else {
          container.innerHTML = "";
        }
      })
      .catch((err) => console.error("Recommendations fetch error:", err));
  }
}
if (!customElements.get("added-to-cart"))
  customElements.define("added-to-cart", AddedToCartDrawer);

class MarketSwitcher extends DrawerElement {
  constructor() {
    super();

    this.languageOptions = this.querySelectorAll(
      `[name="market-switcher__language-input"]`,
    );
    this.countryOptions = this.querySelectorAll(
      `[name="market-switcher__country-input"]`,
    );

    this.form = this.querySelector("form");
    this.formSubmit = this.querySelector("[localization-switch]");
  }

  connectedCallback() {
    super.connectedCallback();

    this.languageOptions.forEach((option) => {
      option.addEventListener("change", (e) => {
        e.preventDefault();
        this.form.language_code.value = e.target.value;
        this._submit();
      });
    });

    this.countryOptions.forEach((option) => {
      option.addEventListener("change", (e) => {
        e.preventDefault();
        this.form.country_code.value = e.target.value;
        this._submit();
      });
    });

    this.formSubmit.addEventListener("click", (e) => {
      e.preventDefault();
      this._submit();
    });
  }

  _submit() {
    if (this.hasAttribute("loading")) return;
    this.setAttribute("loading", "true");
    this.form.submit();
  }
}
if (!customElements.get("market-switcher"))
  customElements.define("market-switcher", MarketSwitcher);

class PredictiveSearch extends DrawerElement {
  constructor() {
    super();
    this.container = this.querySelector("#predictive-search-results");
    this.input = this.querySelector('input[type="search"]');

    if (this.input) {
      this.input.addEventListener(
        "input",
        debounce(this._onInput.bind(this), 300),
      );
    }
  }

  _onInput() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this._close();
      return;
    }

    this._getSearchResults(searchTerm);
  }

  _getSearchResults(searchTerm) {
    this.setAttribute("loading", "true");

    fetch(
      `${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search-results&resources[limit]=10&resources[limit_scope]=each`,
    )
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#predictive-search-results").innerHTML;
        this.container.innerHTML = resultsMarkup;
        this._initSwiper();
        this.setAttribute("loading", "false");
        this._open();
      })
      .catch((error) => {
        this._close();
        throw error;
      });
  }

  _initSwiper() {
    initProductSwiper(this.container.querySelector("swiper-container"));
  }

  _open() {
    this.input.setAttribute("aria-expanded", "true");
  }

  _close() {
    this.input.setAttribute("aria-expanded", "false");
    this.container.innerHTML = "";
    this.removeAttribute("loading");
  }
}
if (!customElements.get("predictive-search"))
  customElements.define("predictive-search", PredictiveSearch);

class MegaMenu extends DrawerElement {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.trigger = this.querySelector("[drawer-trigger]");
    this.dialog = this.querySelector("dialog");

    if (this.dialog) {
      // Manage ARIA expanded state based on native dialog events
      const toggleAria = (isOpen) => {
        if (this.trigger) {
          this.trigger.setAttribute("aria-expanded", isOpen.toString());
        }
      };

      // Native dialog observer
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === "open") {
            toggleAria(this.dialog.hasAttribute("open"));
          }
        });
      });

      observer.observe(this.dialog, { attributes: true });

      // Clean up fallback
      this.dialog.addEventListener("close", () => toggleAria(false));

      // Override native focus trap
      this.dialog.addEventListener("keydown", this._handleKeydown.bind(this));
    }
  }

  _handleKeydown(event) {
    if (event.key !== "Tab") return;

    const focusableElements = Array.from(
      this.dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => el.offsetWidth > 0 || el.offsetHeight > 0);

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey) {
      // Shift + Tab on first element
      if (document.activeElement === firstElement) {
        event.preventDefault();
        this.close();
        this.trigger?.focus();
      }
    } else {
      // Tab on last element
      if (document.activeElement === lastElement) {
        event.preventDefault();
        this.close();

        // Generically find the next logically focusable element on the page after `this.trigger`
        const allFocusableOnPage = Array.from(
          document.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        ).filter(
          (el) =>
            (el.offsetWidth > 0 || el.offsetHeight > 0) &&
            !this.dialog.contains(el),
        );

        const currentIndex = allFocusableOnPage.indexOf(this.trigger);

        if (
          currentIndex !== -1 &&
          currentIndex < allFocusableOnPage.length - 1
        ) {
          allFocusableOnPage[currentIndex + 1].focus();
        }
      }
    }
  }
}
if (!customElements.get("mega-menu"))
  customElements.define("mega-menu", MegaMenu);

class MobileMenu extends DrawerElement {
  constructor() {
    super();
    this.track = this.querySelector(".mobile-menu__track");
    this.stage2 = this.querySelector(".mobile-menu__stage--2");
    this.stage3 = this.querySelector(".mobile-menu__stage--3");

    this.links = this.querySelectorAll(
      "[data-target-panel], .mobile-menu__back",
    );
    this._handleLinkClick = this._handleLinkClick.bind(this);
    this._handleScroll = debounce(this._handleScroll.bind(this), 50);
  }

  connectedCallback() {
    super.connectedCallback();

    this.links.forEach((link) => {
      link.addEventListener("click", this._handleLinkClick);
    });

    if (this.track) {
      this.track.addEventListener("scroll", this._handleScroll);
    }
  }

  disconnectedCallback() {
    this.links.forEach((link) => {
      link.removeEventListener("click", this._handleLinkClick);
    });
    if (this.track) {
      this.track.removeEventListener("scroll", this._handleScroll);
    }
  }

  _handleLinkClick(event) {
    event.preventDefault();

    if (event.currentTarget.classList.contains("mobile-menu__back")) {
      // Just visually scroll back. The scroll listener will clean up.
      const currentScroll = this.track.scrollLeft;
      const stageWidth = this.track.clientWidth;

      let targetLeft = 0;
      if (currentScroll > stageWidth * 1.5) {
        targetLeft = stageWidth; // go to stage 2
      } else {
        targetLeft = 0; // go to stage 1
      }

      this.track.scrollTo({
        left: targetLeft,
        behavior: "smooth",
      });
      return;
    }

    const targetPanelId = event.currentTarget.dataset.targetPanel;
    if (!targetPanelId) return;

    const targetPanel = this.querySelector(`[data-panel="${targetPanelId}"]`);
    if (!targetPanel) return;

    const isLevel3 = targetPanel.classList.contains("panel--3");

    // Deactivate siblings in the same stage to prevent overlaps
    if (isLevel3) {
      if (this.stage3) {
        this.stage3
          .querySelectorAll(".panel.is-active")
          .forEach((p) => p.classList.remove("is-active"));
        this.stage3.style.display = "block";
      }
    } else {
      if (this.stage2) {
        this.stage2
          .querySelectorAll(".panel.is-active")
          .forEach((p) => p.classList.remove("is-active"));
        this.stage2.style.display = "block";
      }
    }

    targetPanel.classList.add("is-active");

    // Scroll to the new stage
    const stageWidth = this.track.clientWidth;
    const targetLeft = isLevel3 ? stageWidth * 2 : stageWidth;

    // Give browser a frame to apply display: block
    requestAnimationFrame(() => {
      if (this.track) {
        this.track.scrollTo({
          left: targetLeft,
          behavior: "smooth",
        });
      }
    });
  }

  _handleScroll() {
    if (!this.track) return;

    const scrollLeft = this.track.scrollLeft;
    const stageWidth = this.track.clientWidth;

    // If scrolled back to stage 1
    if (scrollLeft < stageWidth * 0.1) {
      if (this.stage2) this.stage2.style.display = "none";
      if (this.stage3) this.stage3.style.display = "none";
      this.querySelectorAll(".panel--2.is-active, .panel--3.is-active").forEach(
        (p) => p.classList.remove("is-active"),
      );
    }
    // If scrolled back to stage 2
    else if (scrollLeft < stageWidth * 1.1) {
      if (this.stage3) this.stage3.style.display = "none";
      this.querySelectorAll(".panel--3.is-active").forEach((p) =>
        p.classList.remove("is-active"),
      );
    }
  }

  close() {
    super.close();
    // Reset state instantly when closed
    setTimeout(() => {
      if (this.track) this.track.scrollTo({ left: 0, behavior: "instant" });
      if (this.stage2) this.stage2.style.display = "none";
      if (this.stage3) this.stage3.style.display = "none";
      this.querySelectorAll(".panel.is-active").forEach((panel) => {
        if (!panel.classList.contains("panel--1")) {
          panel.classList.remove("is-active");
        }
      });
    }, 300);
  }
}
if (!customElements.get("mobile-menu"))
  customElements.define("mobile-menu", MobileMenu);

class UspsCarousel extends HTMLElement {
  connectedCallback() {
    this.carousel = this.querySelector("swiper-container");
    if (!this.carousel) return;
    this.addCarouselParameters();
  }

  disconnectedCallback() {
    this.carousel?.swiper?.destroy(true, true);
  }

  addCarouselParameters() {
    const slideCount = this.querySelectorAll("swiper-slide").length;

    Object.assign(this.carousel, {
      slidesPerView: 2,
      centeredSlides: true,
      autoHeight: true,
      autoplay: { delay: 1000, disableOnInteraction: false },
      loop: true,
      speed: 800,
      breakpoints: {
        560: {
          slidesPerView: 3,
        },
        767: {
          slidesPerView: slideCount,
          centeredSlides: false,
          autoplay: false,
          loop: false,
          autoHeight: false,
        },
      },
    });

    this.carousel.initialize();
  }
}

if (!customElements.get("usps-carousel"))
  customElements.define("usps-carousel", UspsCarousel);

class ComplementaryProducts extends HTMLElement {
  connectedCallback() {
    this.carousel = this.querySelector("swiper-container");
    if (!this.carousel) return;
    this.addCarouselParameters();
  }

  disconnectedCallback() {
    this.carousel?.swiper?.destroy(true, true);
  }

  addCarouselParameters() {
    Object.assign(this.carousel, {
      slidesPerView: 1.5,
      spaceBetween: 12,
      watchOverflow: true,
      navigation: {
        nextEl: ".product-complementary__nav .carousel-navigation__next",
        prevEl: ".product-complementary__nav .carousel-navigation__prev",
      },
      scrollbar: {
        el: ".product-complementary__scrollbar",
        draggable: true,
      },
      breakpoints: {
        768: {
          slidesPerView: 2.5,
          spaceBetween: 16,
        },
      },
      on: {
        afterInit: stripSwiperAriaControls,
      },
    });

    this.carousel.initialize();
  }
}

if (!customElements.get("complementary-products"))
  customElements.define("complementary-products", ComplementaryProducts);

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    this.load();
  }

  async load() {
    const { url } = this.dataset;
    if (!url) return;

    const response = await fetch(url);
    if (!response.ok) return;

    const text = await response.text();
    const doc = new DOMParser().parseFromString(text, "text/html");
    const carousel = doc.querySelector(".product-recommendations__carousel");
    if (!carousel || !carousel.children.length) return;

    this.querySelector(".product-recommendations__carousel").replaceWith(
      carousel,
    );

    const swiper = this.querySelector("swiper-container");
    if (!swiper) return;

    initRecommendationsSwiper(swiper, {
      container: this.querySelector(".container"),
      navPrev: ".product-recommendations__nav .carousel-navigation__prev",
      navNext: ".product-recommendations__nav .carousel-navigation__next",
      breakpoints: {
        520: { slidesPerView: 2.5 },
        992: { slidesPerView: 3.5, spaceBetween: 24 },
        1400: { slidesPerView: 4.5, spaceBetween: 32 },
      },
    });
  }
}

if (!customElements.get("product-recommendations"))
  customElements.define("product-recommendations", ProductRecommendations);

class LayoutSwitcher extends HTMLElement {
  constructor() {
    super();
    this.buttons = this.querySelectorAll("button[data-layout]");
    this.layoutKey = "nizio:grid-layout";
    this.urlParam = "layout";

    this.currentLayout = this._readLayout();
    this.updateActiveButton();

    this.buttons.forEach((button) => {
      button.addEventListener("click", () => {
        this.currentLayout = button.dataset.layout;
        this._persistLayout(this.currentLayout);
        this.updateActiveButton();

        window.dispatchEvent(
          new CustomEvent("layout-switcher:change", {
            detail: { layout: this.currentLayout },
          }),
        );
      });
    });
  }

  _readLayout() {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get(this.urlParam) ||
      localStorage.getItem(this.layoutKey) ||
      "layout-compact"
    );
  }

  _persistLayout(layout) {
    localStorage.setItem(this.layoutKey, layout);
    const params = new URLSearchParams(window.location.search);
    params.set(this.urlParam, layout);
    history.replaceState(null, "", "?" + params.toString());
  }

  updateActiveButton() {
    this.buttons.forEach((btn) => {
      const isActive = btn.dataset.layout === this.currentLayout;
      btn.classList.toggle("is-active", isActive);
      btn.setAttribute("aria-pressed", isActive.toString());
    });
  }
}

if (!customElements.get("layout-switcher"))
  customElements.define("layout-switcher", LayoutSwitcher);

// ─── VideoManager ─────────────────────────────────────────────────────────────
class VideoManager {
  constructor(root) {
    this._root = root;
    this._rafId = null;
    this._bound = this._schedule.bind(this);
  }

  start() {
    window.addEventListener("scroll", this._bound, { passive: true });
    window.addEventListener("resize", this._bound, { passive: true });
    this._update();
  }

  stop() {
    window.removeEventListener("scroll", this._bound);
    window.removeEventListener("resize", this._bound);
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  _schedule() {
    if (this._rafId) return;
    this._rafId = requestAnimationFrame(() => {
      this._update();
      this._rafId = null;
    });
  }

  _update() {
    const cards = [...this._root.querySelectorAll("card-video")];
    const vh = window.innerHeight;
    const rowMap = new Map();

    for (const card of cards) {
      const rect = card.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= vh) continue;
      const key = Math.round(rect.top / 10) * 10;
      if (!rowMap.has(key))
        rowMap.set(key, { top: rect.top, bottom: rect.bottom, items: [] });
      rowMap.get(key).items.push(card);
    }

    if (!rowMap.size) {
      cards.forEach((c) => c._pause());
      return;
    }

    // Dominujący wiersz: max widocznych pikseli
    let best = null,
      bestPx = 0;
    for (const row of rowMap.values()) {
      const px = Math.min(row.bottom, vh) - Math.max(row.top, 0);
      if (px > bestPx) {
        bestPx = px;
        best = row;
      }
    }

    // Sortuj left→right, wyznacz strefę
    best.items.sort(
      (a, b) => a.getBoundingClientRect().left - b.getBoundingClientRect().left,
    );
    const N = best.items.length;
    const cy = Math.max(0, Math.min((best.top + best.bottom) / 2, vh));
    const zone = Math.min(Math.floor((1 - cy / vh) * N), N - 1);
    const winner = best.items[zone] ?? best.items[0];

    cards.forEach((c) => (c === winner ? c._play() : c._pause()));
  }
}

// ─── CardVideo ────────────────────────────────────────────────────────────────
class CardVideo extends HTMLElement {
  connectedCallback() {
    this._video = this.querySelector("video");
    this._hovered = false;
    this.addEventListener("mouseenter", () => {
      this._hovered = true;
      this._pause();
    });
    this.addEventListener("mouseleave", () => {
      this._hovered = false;
      this._play();
    });
  }

  _play() {
    if (
      !this._video ||
      this._hovered ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    )
      return;
    this._video.play().catch(() => {});
  }

  _pause() {
    if (!this._video) return;
    this._video.pause();
    this._video.currentTime = 0;
  }
}

if (!customElements.get("card-video"))
  customElements.define("card-video", CardVideo);

class CollectionGrid extends HTMLElement {
  constructor() {
    super();
    this.layoutKey = "nizio:grid-layout";
    this.urlParam = "layout";
    this.currentLayout =
      new URLSearchParams(window.location.search).get(this.urlParam) ||
      localStorage.getItem(this.layoutKey) ||
      "layout-compact";

    this.updateLayoutModifier();

    window.addEventListener("layout-switcher:change", (event) => {
      this.currentLayout = event.detail.layout;
      this.updateLayoutModifier();
    });
  }

  connectedCallback() {
    if (this.querySelector("card-video")) {
      this._videoManager = new VideoManager(this);
      this._videoManager.start();
    }

    this._loadSwatches();
    // Filtering and sorting swap the grid's contents for fresh skeletons.
    this._swatchesObserver = new MutationObserver(() => this._loadSwatches());
    this._swatchesObserver.observe(this, { childList: true });
  }

  disconnectedCallback() {
    this._videoManager?.stop();
    this._swatchesObserver?.disconnect();
    this._swatchesAbort?.abort();
  }

  // Swatches load every product of each card's group, so they are fetched for
  // the whole page in one request after it rather than holding up its render.
  async _loadSwatches() {
    const placeholders = [...this.querySelectorAll("[data-swatches-for]")];
    if (!placeholders.length) return;

    this._swatchesAbort?.abort();
    const controller = (this._swatchesAbort = new AbortController());

    // A swapped listing is only paginated, filtered and sorted on its own URL.
    const url = new URL(this.dataset.sourceUrl || location.pathname, location.origin);
    url.search = location.search;
    url.searchParams.set("section_id", this.dataset.swatchesSection);

    let doc = null;
    try {
      const response = await fetch(url, { signal: controller.signal });
      if (response.ok) {
        doc = new DOMParser().parseFromString(await response.text(), "text/html");
      }
    } catch (error) {
      if (error.name === "AbortError") return;
    }

    // A product alone in its group gets no swatches, and neither does any card
    // when the request fails — a skeleton must not be left pulsing for good.
    for (const placeholder of placeholders) {
      if (!placeholder.isConnected) continue;
      const swatches = doc?.querySelector(
        `[data-product-id="${placeholder.dataset.swatchesFor}"]`,
      );
      if (swatches) placeholder.replaceWith(swatches);
      else placeholder.remove();
    }
  }

  updateLayoutModifier() {
    if (this.currentLayout === "layout-compact") {
      this.classList.add("collection-grid--compact");
    } else {
      this.classList.remove("collection-grid--compact");
    }
  }
}

if (!customElements.get("collection-grid"))
  customElements.define("collection-grid", CollectionGrid);

class NewsletterSignup extends HTMLElement {
  connectedCallback() {
    this._form = this.querySelector("form");
    if (!this._form) return;
    this._boundHandleSubmit = this._handleSubmit.bind(this);
    this._form.addEventListener("submit", this._boundHandleSubmit);
  }

  disconnectedCallback() {
    this._form?.removeEventListener("submit", this._boundHandleSubmit);
  }

  // ─── Event handlers ────────────────────────────────────────────────────────

  _handleSubmit(event) {
    const form = this._form;
    const emailInput = form.querySelector('input[type="email"]');
    const emailField = emailInput?.closest(".field");
    const consentInput = form.querySelector('input[type="checkbox"]');
    const consentField = consentInput?.closest(".field");

    if (!this._validate(emailInput, emailField)) {
      event.preventDefault();
      return;
    }
    if (!this._validate(consentInput, consentField, (el) => el.checked)) {
      event.preventDefault();
      return;
    }

    // Route the native submit (with hCaptcha token) into a hidden iframe
    if (!this._iframe) {
      const iframe = document.createElement("iframe");
      iframe.name = `newsletter-frame-${this._form.id}`;
      iframe.hidden = true;
      this.appendChild(iframe);
      this._iframe = iframe;

      iframe.addEventListener("load", () => {
        try {
          const url = iframe.contentWindow?.location.href || "";
          if (url.includes("customer_posted=true")) {
            this._showStatus("success");
          } else if (url !== "about:blank") {
            this._showStatus("error");
          }
        } catch {
          this._showStatus("success");
        } finally {
          form
            .querySelector('button[type="submit"]')
            ?.toggleAttribute("data-loading", false);
        }
      });
    }

    form.target = this._iframe.name;
    form
      .querySelector('button[type="submit"]')
      ?.toggleAttribute("data-loading", true);

    // Don't preventDefault — let hCaptcha and native submit proceed into the iframe
  }

  // ─── Validation ────────────────────────────────────────────────────────────

  _validate(input, field, test = (el) => el.value.trim().length > 0) {
    if (!input) return true;

    const valid = test(input);
    field?.classList.toggle("invalid", !valid);
    input.setAttribute("aria-invalid", valid ? "false" : "true");
    if (!valid) input.focus();
    return valid;
  }

  // ─── State helpers ─────────────────────────────────────────────────────────

  _showStatus(type) {
    const selector =
      type === "success"
        ? ".newsletter__success.hidden"
        : ".newsletter__error.hidden";
    const panel = this.querySelector(selector);
    if (!panel) return;

    this.querySelectorAll(".newsletter__header, .newsletter__form").forEach(
      (el) => {
        el.setAttribute("aria-hidden", "true");
        el.classList.add("newsletter__region--hidden");
      },
    );

    panel.classList.remove("hidden");
    panel.removeAttribute("aria-hidden");
    panel.setAttribute("aria-live", "polite");
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        panel.classList.add("newsletter__status--visible");
      });
    });

    const heading = panel.querySelector("h2, h3");
    if (heading) {
      heading.setAttribute("tabindex", "-1");
      heading.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get("newsletter-signup"))
  customElements.define("newsletter-signup", NewsletterSignup);

class CollectionsCarousel extends HTMLElement {
  connectedCallback() {
    this.carousel = this.querySelector("swiper-container");
    if (!this.carousel) return;
    this.addCarouselParameters();
  }

  addCarouselParameters() {
    Object.assign(this.carousel, {
      slidesPerView: "auto",
      spaceBetween: 24,
      centeredSlides: true,
      mousewheel: { enabled: true, forceToAxis: true },
      navigation: {
        nextEl: ".collections-carousel__nav .carousel-navigation__next",
        prevEl: ".collections-carousel__nav .carousel-navigation__prev",
      },
      on: {
        init: (swiper) => {
          swiper.el.style.overflow = "visible";
          swiper.params.centeredSlides = false;
          swiper.update();
        },
        afterInit: stripSwiperAriaControls,
      },
    });

    this.carousel.initialize();
  }
}

if (!customElements.get("collections-carousel"))
  customElements.define("collections-carousel", CollectionsCarousel);

class ReviewsCarousel extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.carousel = this.querySelector("swiper-container");
    if (!this.carousel) return;

    Object.assign(this.carousel, {
      slidesPerView: 1,
      autoplay: { delay: 2000, disableOnInteraction: false },
      loop: true,
      speed: 600,
      pagination: { clickable: true },
      breakpoints: {
        768: {
          slidesPerView: 2,
          autoplay: false,
          loop: false,
        },
      },
    });

    this.carousel.initialize();
    this._syncTrustmate();
  }

  _syncTrustmate() {
    const card = this.querySelector('[data-provider="trustmate"]');
    if (!card) return;

    const grade = document.querySelector(".tm-dodo2__grade");
    if (grade) {
      this._applyRating(card, grade.textContent.trim());
      return;
    }

    this._observer = new MutationObserver(() => {
      const el = document.querySelector(".tm-dodo2__grade");
      if (!el) return;
      this._observer.disconnect();
      this._applyRating(card, el.textContent.trim());
    });
    this._observer.observe(document.body, { childList: true, subtree: true });
  }

  _applyRating(card, raw) {
    const rating = parseFloat(raw.replace(",", "."));
    if (isNaN(rating)) return;

    const score = card.querySelector(".reviews__score");
    const stars = card.querySelector(".reviews__stars");
    if (score) score.textContent = rating;
    if (stars) stars.style.setProperty("--rating-pct", `${rating * 20}%`);
  }

  disconnectedCallback() {
    this._observer?.disconnect();
    this.carousel?.swiper?.destroy(true, true);
  }
}

if (!customElements.get("reviews-carousel"))
  customElements.define("reviews-carousel", ReviewsCarousel);

class SelectInput extends HTMLElement {
  constructor() {
    super();

    this.details = this.querySelector("details");
    this.summary = this.querySelector("summary");
    this.nativeSelect = this.querySelector("select");
    this.displayLabel = this.querySelector(".select-input__label");
    this.displayValue = this.querySelector(".select-input__value");
    this.optionButtons = this.querySelectorAll("[data-value]");

    this.activeIndex = -1;
    this.selectedIndex = -1;
    this.focusableElements = [];
    this.placeholder =
      this.displayValue?.getAttribute("data-placeholder") || "";

    this._handleToggle = this._handleToggle.bind(this);
    this._handleKeydown = this._handleKeydown.bind(this);
    this._handleOptionClick = this._handleOptionClick.bind(this);
    this._handleGlobalOpen = this._handleGlobalOpen.bind(this);
    this._trapFocus = this._trapFocus.bind(this);
  }

  connectedCallback() {
    if (this.nativeSelect?.value) {
      this.updateDisplay(this.nativeSelect.value);
    }

    this.details.addEventListener("toggle", this._handleToggle);
    window.addEventListener("select-input:open", this._handleGlobalOpen);
    this.addEventListener("keydown", this._handleKeydown);

    this.optionButtons.forEach((button, index) => {
      button.addEventListener("click", this._handleOptionClick);

      if (
        this.nativeSelect &&
        button.dataset.value === this.nativeSelect.value
      ) {
        this.activeIndex = index;
      }
    });

    this.setSelectedIndex(this.activeIndex);

    this.updateFocusableElements();
  }

  disconnectedCallback() {
    this.details.removeEventListener("toggle", this._handleToggle);
    window.removeEventListener("select-input:open", this._handleGlobalOpen);
    this.removeEventListener("keydown", this._handleKeydown);

    this.optionButtons.forEach((button) => {
      button.removeEventListener("click", this._handleOptionClick);
    });
  }

  _handleGlobalOpen(event) {
    if (event.detail.target !== this && this.details.open) {
      this.close();
    }
  }

  _handleToggle(event) {
    if (this.details.open) {
      this.onOpen();
    } else {
      this.onClose();
    }
  }

  _handleKeydown(event) {
    if (!this.details.open) return;

    const { key } = event;

    switch (key) {
      case "ArrowDown":
        event.preventDefault();
        this.moveActiveIndex(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        this.moveActiveIndex(-1);
        break;
      case "Home":
        event.preventDefault();
        this.setActiveIndex(0);
        break;
      case "End":
        event.preventDefault();
        this.setActiveIndex(this.optionButtons.length - 1);
        break;
      case "Enter":
      case " ":
        if (event.target === this.summary) return;
        event.preventDefault();
        this.selectActiveOption();
        break;
      case "Escape":
        event.preventDefault();
        this.close();
        break;
      case "Tab":
        this._trapFocus(event);
        break;
      default:
        if (key.length === 1) {
          this.handleTypeAhead(key);
        }
        break;
    }
  }

  _handleOptionClick(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const value = button.dataset.value;
    const label = button.textContent.trim();

    this.selectOption(value, label);
  }

  _trapFocus(event) {
    if (this.focusableElements.length === 0) return;

    const firstElement = this.focusableElements[0];
    const lastElement =
      this.focusableElements[this.focusableElements.length - 1];

    if (event.shiftKey) {
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  updateFocusableElements() {
    this.focusableElements = Array.from(
      this.querySelectorAll(
        'summary, button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    );
  }

  onOpen() {
    this.classList.add("select-input--open");

    if (this.activeIndex < 0) {
      this.setActiveIndex(0);
    } else {
      this.setActiveIndex(this.activeIndex);
    }

    setTimeout(() => {
      if (this.activeIndex >= 0 && this.optionButtons[this.activeIndex]) {
        this.optionButtons[this.activeIndex].focus();
      }
    }, 0);

    window.dispatchEvent(
      new CustomEvent("select-input:open", {
        detail: { target: this },
      }),
    );
  }

  onClose() {
    this.classList.remove("select-input--open");
    this.summary.focus();
  }

  close() {
    this.details.open = false;
  }

  moveActiveIndex(direction) {
    let newIndex = this.activeIndex + direction;

    if (newIndex < 0) {
      newIndex = this.optionButtons.length - 1;
    } else if (newIndex >= this.optionButtons.length) {
      newIndex = 0;
    }

    this.setActiveIndex(newIndex);
  }

  setActiveIndex(index) {
    if (this.activeIndex >= 0 && this.optionButtons[this.activeIndex]) {
      this.optionButtons[this.activeIndex].classList.remove(
        "select-input__option--active",
        "choice-list__item--current",
      );
    }

    this.activeIndex = index;

    if (this.activeIndex >= 0 && this.optionButtons[this.activeIndex]) {
      const activeButton = this.optionButtons[this.activeIndex];
      activeButton.classList.add(
        "select-input__option--active",
        "choice-list__item--current",
      );
      activeButton.focus();

      activeButton.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }

  selectActiveOption() {
    if (this.activeIndex >= 0 && this.optionButtons[this.activeIndex]) {
      const button = this.optionButtons[this.activeIndex];
      const value = button.dataset.value;
      const label = button.textContent.trim();

      this.selectOption(value, label);
    }
  }

  selectOption(value, label) {
    if (this.nativeSelect) {
      this.nativeSelect.value = value;

      const changeEvent = new Event("change", { bubbles: true });
      this.nativeSelect.dispatchEvent(changeEvent);
    }

    this.updateDisplay(value);

    this.optionButtons.forEach((button, index) => {
      if (button.dataset.value === value) {
        this.activeIndex = index;
      }
    });

    this.setSelectedIndex(this.activeIndex);

    this.close();

    const selectEvent = new CustomEvent("select-change", {
      bubbles: true,
      detail: { value, label },
    });
    this.dispatchEvent(selectEvent);
  }

  updateDisplay(value) {
    if (!this.displayValue) return;

    const button = Array.from(this.optionButtons).find(
      (btn) => btn.dataset.value === value,
    );

    if (value && button) {
      this.displayValue.innerHTML = button.innerHTML;
      this.classList.remove("select-input--empty");
      this.classList.add("is-selected");
    } else {
      this.classList.add("select-input--empty");
      this.classList.remove("is-selected");
      this.displayValue.textContent = this.placeholder;
    }
  }

  setSelectedIndex(index) {
    this.optionButtons.forEach((button) => {
      button.classList.remove("choice-list__item--current");
    });

    this.selectedIndex = index;

    if (this.selectedIndex >= 0 && this.optionButtons[this.selectedIndex]) {
      this.optionButtons[this.selectedIndex].classList.add(
        "choice-list__item--current",
      );
    }
  }

  handleTypeAhead(key) {
    const startIndex = (this.activeIndex + 1) % this.optionButtons.length;

    for (let i = 0; i < this.optionButtons.length; i++) {
      const index = (startIndex + i) % this.optionButtons.length;
      const buttonText = this.optionButtons[index].textContent
        .trim()
        .toLowerCase();

      if (buttonText.startsWith(key.toLowerCase())) {
        this.setActiveIndex(index);
        break;
      }
    }
  }
}

if (!customElements.get("select-input"))
  customElements.define("select-input", SelectInput);

/**
 * CollectionFilters – storefront filtering via Section Rendering API.
 *
 * Extends DrawerElement to inherit the drawer open/close behaviour.
 * Works for both collection pages and search results — reads target
 * element IDs from data attributes:
 *   data-grid-id   → grid element to update  (default: "CollectionGrid")
 *   data-actions-id → actions bar element     (default: "CollectionActions")
 *   data-base-url  → base URL for reset/price (e.g. collection.url or /search?q=…&type=product)
 */
class CollectionFilters extends DrawerElement {
  constructor() {
    super();

    this._handleFilterClick = this._handleFilterClick.bind(this);
    this._handleResetClick = this._handleResetClick.bind(this);
    this._handlePriceInput = debounce(this._handlePriceInput.bind(this), 500);
    this._handlePopState = this._handlePopState.bind(this);
    this._scheduleApply = debounce(this._applyPending.bind(this), 350);
    this._pendingUrl = null;
  }

  connectedCallback() {
    super.connectedCallback();

    this.drawerContents = this.querySelector(".drawer__contents");

    const gridId = this.dataset.gridId || "CollectionGrid";
    const actionsId = this.dataset.actionsId || "CollectionActions";

    this.gridEl = document.querySelector(`#${gridId}`);
    this.actionsEl = document.querySelector(`#${actionsId}`);
    this.sectionId = this.gridEl?.dataset.sectionId;
    this.sourceUrl = this.gridEl?.dataset.sourceUrl;

    this.drawerContents?.addEventListener("click", this._handleFilterClick);

    this.resetBtn = this.querySelector("[data-filter-reset]");
    this.resetBtn?.addEventListener("click", this._handleResetClick);

    this.drawerContents?.addEventListener("input", this._handlePriceInput);

    window.addEventListener("popstate", this._handlePopState);

    // The server ignores filters and sorting on a listing borrowed from another
    // collection, so a deep link to a filtered view has to be fetched again.
    if (this.sourceUrl && this._hasListingParams(window.location.href)) {
      this._applyFilter(window.location.href, false);
    }
  }

  disconnectedCallback() {
    this.drawerContents?.removeEventListener("click", this._handleFilterClick);
    this.resetBtn?.removeEventListener("click", this._handleResetClick);
    this.drawerContents?.removeEventListener("input", this._handlePriceInput);
    window.removeEventListener("popstate", this._handlePopState);
  }

  _handleFilterClick(event) {
    const link = event.target.closest("[data-filter-link]");
    if (!link) return;

    event.preventDefault();
    if (this.getAttribute("loading") === "true") return;

    // Optimistic UI: toggle current state on the clicked item
    if (link.classList.contains("choice-list__item")) {
      link.classList.toggle("choice-list__item--current");
    }

    // Compose the diff between window.location and link.href onto pending URL
    const diff = this._urlDiff(window.location.href, link.href);
    const base = this._pendingUrl || window.location.href;
    this._pendingUrl = this._applyDiff(base, diff);

    this._scheduleApply();
  }

  _applyPending() {
    if (!this._pendingUrl) return;
    const url = this._pendingUrl;
    this._pendingUrl = null;
    this._applyFilter(url);
  }

  _urlDiff(fromUrl, toUrl) {
    const from = [
      ...new URL(fromUrl, window.location.origin).searchParams.entries(),
    ];
    const to = [
      ...new URL(toUrl, window.location.origin).searchParams.entries(),
    ];
    const eq = ([a, b], [c, d]) => a === c && b === d;
    const added = to.filter((p) => !from.some((f) => eq(f, p)));
    // No filter link ever carries the colour param, so a plain diff would read
    // it as removed on every click and drop the switcher's state.
    const removed = from.filter(
      ([k, v]) => k !== ColorSwitcher.PARAM && !to.some((t) => eq(t, [k, v])),
    );
    return { added, removed };
  }

  _applyDiff(url, { added, removed }) {
    const u = new URL(url, window.location.origin);
    for (const [k, v] of removed) {
      const remaining = u.searchParams.getAll(k).filter((x) => x !== v);
      u.searchParams.delete(k);
      remaining.forEach((x) => u.searchParams.append(k, x));
    }
    for (const [k, v] of added) {
      // Single-value params (sort_by, page, etc.) — replace; multi-value — append
      if (k === "sort_by" || k === "page") {
        u.searchParams.set(k, v);
      } else {
        u.searchParams.append(k, v);
      }
    }
    return u.toString();
  }

  _handleResetClick(event) {
    event.preventDefault();
    if (this.getAttribute("loading") === "true") return;
    this._pendingUrl = null;
    const baseUrl = this.dataset.baseUrl || this.resetBtn.href;
    this._applyFilter(baseUrl);
  }

  _handlePriceInput(event) {
    const priceRange = event.target.closest("[data-price-range]");
    if (!priceRange) return;

    const inputs = priceRange.querySelectorAll("[data-price-number]");
    const url = new URL(window.location.href);

    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith("filter.v.price")) url.searchParams.delete(key);
    }

    inputs.forEach((input) => {
      const raw = input.value.replace(/\s/g, "");
      if (raw !== "" && raw !== "0") {
        url.searchParams.set(input.name, (parseFloat(raw) * 100).toString());
      }
    });

    this._applyFilter(url.toString());
  }

  _handlePopState() {
    this._applyFilter(window.location.href, false);
  }

  _hasListingParams(url) {
    return [...new URL(url, window.location.origin).searchParams.keys()].some(
      (key) => key === "sort_by" || key.startsWith("filter."),
    );
  }

  // Pagination rendered on the source collection's URL has to keep paging the
  // collection the shopper is actually on.
  _relinkPagination() {
    this.gridEl.querySelectorAll(".pagination a[href]").forEach((link) => {
      const href = new URL(link.href, window.location.origin);
      href.pathname = window.location.pathname;
      link.href = href.toString();
    });
  }

  async _applyFilter(url, pushState = true) {
    this._abortController?.abort();
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    this._setLoading(true);

    try {
      const fetchUrl = new URL(url, window.location.origin);
      let sectionId = this.sectionId;
      if (this.sourceUrl) {
        fetchUrl.pathname = new URL(this.sourceUrl, window.location.origin).pathname;
        sectionId = this.gridEl.dataset.sourceSection;
      }
      fetchUrl.searchParams.set("sections", sectionId);

      const response = await fetch(fetchUrl.toString(), { signal });
      if (!response.ok) throw new Error(response.status);

      const json = await response.json();
      const html = json[sectionId];
      if (!html) return;

      const doc = new DOMParser().parseFromString(html, "text/html");

      // Update product grid
      const newGrid = doc.querySelector(`#${this.gridEl.id}`);
      if (newGrid && this.gridEl) {
        this.gridEl.innerHTML = newGrid.innerHTML;
        if (this.sourceUrl) this._relinkPagination();
      }

      // Update filter drawer contents
      const newDrawerContents = doc.querySelector(
        `#${this.id} .drawer__contents`,
      );
      if (newDrawerContents && this.drawerContents) {
        this.drawerContents.innerHTML = newDrawerContents.innerHTML;
      }

      // Update filter drawer footer (result count button)
      const newDrawerFooter = doc.querySelector(`#${this.id} .drawer__footer`);
      const currentFooter = this.querySelector(".drawer__footer");
      if (newDrawerFooter && currentFooter) {
        currentFooter.innerHTML = newDrawerFooter.innerHTML;
        this.resetBtn = this.querySelector("[data-filter-reset]");
        this.resetBtn?.addEventListener("click", this._handleResetClick);
      }

      // Update result count outside the drawer
      const totalsSelector = this.actionsEl
        ? `#${this.actionsEl.id} .collection-actions__totals, #${this.actionsEl.id} .search-actions__totals`
        : null;
      if (totalsSelector) {
        const newTotals = doc.querySelector(totalsSelector);
        const currentTotals = this.actionsEl.querySelector(
          ".collection-actions__totals, .search-actions__totals",
        );
        if (newTotals && currentTotals) {
          currentTotals.innerHTML = newTotals.innerHTML;
        }
      }

      if (pushState) {
        const cleanUrl = new URL(url, window.location.origin);
        cleanUrl.searchParams.delete("sections");
        history.pushState(null, "", cleanUrl.toString());
      }
    } catch (error) {
      if (error.name !== "AbortError")
        console.error("CollectionFilters: fetch error", error);
    } finally {
      if (!signal.aborted) this._setLoading(false);
    }
  }

  _setLoading(state) {
    this.setAttribute("loading", state ? "true" : "false");
    if (this.gridEl) {
      this.gridEl.setAttribute("loading", state ? "true" : "false");
    }
  }
}

if (!customElements.get("collection-filters"))
  customElements.define("collection-filters", CollectionFilters);

if (!customElements.get("search-filters"))
  customElements.define("search-filters", class extends CollectionFilters {});

class PriceRangeSlider extends HTMLElement {
  connectedCallback() {
    this.thumbMin = this.querySelector("[data-slider-min]");
    this.thumbMax = this.querySelector("[data-slider-max]");
    this.fill = this.querySelector("[data-slider-fill]");
    const numbers = this.querySelectorAll("[data-price-number]");
    this.inputMin = numbers[0];
    this.inputMax = numbers[1];

    if (!this.thumbMin || !this.thumbMax || !this.inputMin || !this.inputMax)
      return;

    if (this.inputMin.value)
      this.inputMin.value = this._format(this.inputMin.value);
    if (this.inputMax.value)
      this.inputMax.value = this._format(this.inputMax.value);

    this._updateFill();
    this.thumbMin.addEventListener("input", () => this._onThumbMin());
    this.thumbMax.addEventListener("input", () => this._onThumbMax());
    this.inputMin.addEventListener("change", () =>
      this._onNumberChange(this.inputMin),
    );
    this.inputMax.addEventListener("change", () =>
      this._onNumberChange(this.inputMax),
    );
    [this.inputMin, this.inputMax].forEach((el) => {
      el.addEventListener("keydown", (e) => this._blockNonNumeric(e));
      el.addEventListener("input", () => {
        const digits = el.value.replace(/\D/g, "");
        if (digits !== el.value) el.value = digits;
      });
    });
    this.inputMin.addEventListener("blur", () => {
      if (this.inputMin.value)
        this.inputMin.value = this._format(this._parse(this.inputMin.value));
    });
    this.inputMax.addEventListener("blur", () => {
      if (this.inputMax.value)
        this.inputMax.value = this._format(this._parse(this.inputMax.value));
    });
  }

  _blockNonNumeric(e) {
    const allowed = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Tab",
      "Home",
      "End",
    ];
    if (allowed.includes(e.key)) return;
    if (e.ctrlKey || e.metaKey) return;
    if (!/^\d$/.test(e.key)) e.preventDefault();
  }

  _format(val) {
    const n = Math.round(
      parseFloat(String(val).replace(/\s/g, "").replace(",", ".")) || 0,
    );
    return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  }

  _parse(str) {
    return parseFloat(String(str).replace(/\s/g, "").replace(",", ".")) || 0;
  }

  _onThumbMin() {
    const min = parseFloat(this.thumbMin.value);
    const max = parseFloat(this.thumbMax.value);
    if (min > max) this.thumbMin.value = max;
    const val = parseFloat(this.thumbMin.value);
    this.inputMin.value = val === 0 ? "" : this._format(val);
    this.inputMin.dispatchEvent(new Event("input", { bubbles: true }));
    this._updateFill();
  }

  _onThumbMax() {
    const min = parseFloat(this.thumbMin.value);
    const max = parseFloat(this.thumbMax.value);
    if (max < min) this.thumbMax.value = min;
    const val = parseFloat(this.thumbMax.value);
    this.inputMax.value =
      val >= parseFloat(this.thumbMax.max) ? "" : this._format(val);
    this.inputMax.dispatchEvent(new Event("input", { bubbles: true }));
    this._updateFill();
  }

  _onNumberChange(input) {
    const minVal = this._parse(this.inputMin.value);
    const maxVal =
      this._parse(this.inputMax.value) || parseFloat(this.thumbMax.max);
    this.thumbMin.value = minVal;
    this.thumbMax.value = maxVal;
    this._updateFill();
    input.dispatchEvent(new Event("input", { bubbles: true }));
  }

  _updateFill() {
    const max = parseFloat(this.thumbMin.max);
    const minVal = parseFloat(this.thumbMin.value) || 0;
    const maxVal = parseFloat(this.thumbMax.value) || max;
    this.fill.style.left = `${(minVal / max) * 100}%`;
    this.fill.style.right = `${((max - maxVal) / max) * 100}%`;
  }
}

if (!customElements.get("price-range-slider"))
  customElements.define("price-range-slider", PriceRangeSlider);

class ProductLightbox extends HTMLElement {
  constructor() {
    super();
    this.dialog = this.querySelector("dialog.product-lightbox");
    this.slides = null;
    this.currentIndex = 0;
    this.videoAutoplay = this.hasAttribute("data-video-autoplay");
    this._handleKeydown = this._handleKeydown.bind(this);
    this._mobileMQ = window.matchMedia("(max-width: 860px)");
    this._onMQChange = this._onMQChange.bind(this);
    this._mobileSwiper = null;
  }

  connectedCallback() {
    this.querySelectorAll("[data-lightbox-trigger]").forEach((trigger) => {
      trigger.addEventListener("click", () => {
        if (this._mobileMQ.matches) return;
        this.openAt(parseInt(trigger.dataset.index, 10));
      });
    });

    const expandBtn = this.querySelector("[data-gallery-expand]");
    if (expandBtn) {
      const gallery = this.querySelector(".product-main__gallery");
      const limit = parseInt(expandBtn.dataset.galleryLimit, 10);
      const items = gallery.querySelectorAll(".product-main__gallery-item");

      requestAnimationFrame(() => {
        const firstHidden = items[limit];
        if (!firstHidden) return;
        gallery.style.maxHeight = firstHidden.offsetTop + "px";
        gallery.style.overflow = "hidden";
      });

      expandBtn.addEventListener("click", () => {
        gallery.style.maxHeight = gallery.scrollHeight + "px";
        gallery.addEventListener(
          "transitionend",
          () => {
            gallery.style.maxHeight = "";
            gallery.style.overflow = "";
          },
          { once: true },
        );
        expandBtn.remove();
      });
    }

    this._mobileMQ.addEventListener("change", this._onMQChange);
    this._onMQChange();

    this.querySelector("[data-lightbox-close]")?.addEventListener("click", () =>
      this.close(),
    );

    this.querySelector("[data-lightbox-prev]")?.addEventListener("click", () =>
      this._navigate(-1),
    );

    this.querySelector("[data-lightbox-next]")?.addEventListener("click", () =>
      this._navigate(1),
    );

    this.dialog?.addEventListener("click", (e) => {
      if (e.target.closest("button")) return;
      const slide = this.slides?.[this.currentIndex];
      const img = slide?.querySelector("img.product-lightbox__image");
      if (img) {
        const r = img.getBoundingClientRect();
        if (
          e.clientX >= r.left &&
          e.clientX <= r.right &&
          e.clientY >= r.top &&
          e.clientY <= r.bottom
        ) {
          const zooming = slide.classList.toggle("is-zoomed");
          if (zooming) {
            const scale = Math.max(1.5, img.naturalWidth / r.width);
            img.style.transform = `scale(${scale})`;
            const ox = ((e.clientX - r.left) / r.width) * 100;
            const oy = ((e.clientY - r.top) / r.height) * 100;
            img.style.transformOrigin = `${ox}% ${oy}%`;
          } else {
            img.style.transform = "";
            img.style.transformOrigin = "";
          }
          return;
        }
      }
      this.close();
    });

    this.dialog?.addEventListener("mousemove", (e) => {
      const slide = this.slides?.[this.currentIndex];
      if (!slide?.classList.contains("is-zoomed")) return;
      const img = slide.querySelector("img.product-lightbox__image");
      if (!img) return;
      const r = slide.getBoundingClientRect();
      const ox = ((e.clientX - r.left) / r.width) * 100;
      const oy = ((e.clientY - r.top) / r.height) * 100;
      img.style.transformOrigin = `${ox}% ${oy}%`;
    });

    this.dialog?.addEventListener("keydown", this._handleKeydown);

    this.dialog?.addEventListener("close", () => {
      this._pauseMedia(this.currentIndex);
      if (this.videoAutoplay) this._syncToGrid(this.currentIndex);
      this._visibleGalleryVideos?.forEach((v) => v.play());
    });
  }

  // Desktop-only: autoplay grid gallery videos (muted, in their tile) while
  // visible. Started/stopped from _onMQChange so the mobile swiper, which has
  // its own per-slide playback, is never touched.
  _startGridAutoplay() {
    if (this._gridVideoObserver) return;
    const videos = this.querySelectorAll(
      ".product-main__gallery [data-gallery-video]",
    );
    if (!videos.length) return;
    this._visibleGalleryVideos = new Set();
    this._gridVideoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          const item = video.closest(".product-main__gallery-item");
          if (entry.isIntersecting) {
            this._visibleGalleryVideos.add(video);
            if (!this.dialog?.open) {
              if (video.readyState === 0) video.load();
              video
                .play()
                .then(() => item?.classList.add("is-playing"))
                .catch(() => {});
            }
          } else {
            this._visibleGalleryVideos.delete(video);
            video.pause();
            item?.classList.remove("is-playing");
          }
        });
      },
      { threshold: 0.5 },
    );
    videos.forEach((v) => this._gridVideoObserver.observe(v));
  }

  _stopGridAutoplay() {
    this._gridVideoObserver?.disconnect();
    this._gridVideoObserver = null;
    this._visibleGalleryVideos?.forEach((v) => v.pause());
    this._visibleGalleryVideos = null;
  }

  _gridVideoAt(index) {
    const trigger = this.querySelector(
      `.product-main__gallery [data-lightbox-trigger][data-index="${index}"]`,
    );
    return trigger?.querySelector("[data-gallery-video]") ?? null;
  }

  _syncToGrid(index) {
    const gridVideo = this._gridVideoAt(index);
    const slideVideo = this.slides?.[index]?.querySelector("video");
    if (!gridVideo || !slideVideo) return;
    try {
      gridVideo.currentTime = slideVideo.currentTime;
    } catch {}
  }

  openAt(index) {
    this.slides = Array.from(this.querySelectorAll(".product-lightbox__slide"));
    if (!this.slides.length) return;
    this.currentIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    this._visibleGalleryVideos?.forEach((v) => v.pause());
    // Carry the grid video's playhead into the lightbox so they stay in sync.
    const gridVideo = this.videoAutoplay
      ? this._gridVideoAt(this.currentIndex)
      : null;
    this._syncGridTime = gridVideo ? gridVideo.currentTime : null;
    this._showSlide(this.currentIndex);
    this.dialog.showModal();
  }

  close() {
    this._pauseMedia(this.currentIndex);
    this.dialog?.close();
  }

  _navigate(dir) {
    const next = this.currentIndex + dir;
    if (next < 0 || next >= this.slides.length) return;
    this._pauseMedia(this.currentIndex);
    this.currentIndex = next;
    this._showSlide(this.currentIndex);
  }

  _showSlide(index) {
    this.slides.forEach((slide, i) => {
      slide.hidden = i !== index;
      slide.classList.remove("is-zoomed");
      const img = slide.querySelector(".product-lightbox__image");
      if (img) {
        img.style.transform = "";
        img.style.transformOrigin = "";
      }
      if (i === index) {
        this._activateMedia(slide);
        this._startLoading(slide);
      }
    });
    this._updateNav();
  }

  _startLoading(slide) {
    const img = slide.querySelector(".product-lightbox__image");
    if (!img) return;
    if (img.complete) {
      img.style.opacity = "1";
      return;
    }
    slide.classList.add("is-loading");
    const done = () => {
      slide.classList.remove("is-loading");
      img.style.opacity = "1";
    };
    img.addEventListener("load", done, { once: true });
    img.addEventListener("error", done, { once: true });
  }

  _activateMedia(slide) {
    const iframe = slide.querySelector("iframe[data-src]");
    if (iframe) {
      iframe.src = iframe.dataset.src;
      delete iframe.dataset.src;
    }
    const video = slide.querySelector("video");
    if (video) {
      if (video.readyState === 0) video.load();
      const seekTo = this._syncGridTime;
      this._syncGridTime = null;
      const play = () => {
        if (seekTo != null) {
          try {
            video.currentTime = seekTo;
          } catch {}
        }
        video.play();
      };
      if (seekTo != null && video.readyState < 1) {
        video.addEventListener("loadedmetadata", play, { once: true });
      } else {
        play();
      }
    }
  }

  _pauseMedia(index) {
    const slide = this.slides?.[index];
    if (!slide) return;
    slide.querySelector("video")?.pause();
    const iframe = slide.querySelector("iframe[src]");
    if (iframe) {
      iframe.dataset.src = iframe.src;
      iframe.removeAttribute("src");
    }
  }

  _updateNav() {
    const total = this.slides?.length ?? 0;
    const prev = this.querySelector("[data-lightbox-prev]");
    const next = this.querySelector("[data-lightbox-next]");
    const counter = this.querySelector("[data-lightbox-counter]");
    if (prev) prev.disabled = this.currentIndex === 0;
    if (next) next.disabled = this.currentIndex === total - 1;
    if (counter) counter.textContent = `${this.currentIndex + 1} / ${total}`;
  }

  _handleKeydown(e) {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      this._navigate(-1);
    }
    if (e.key === "ArrowRight") {
      e.preventDefault();
      this._navigate(1);
    }
  }

  _onMQChange() {
    if (this._mobileMQ.matches) {
      this._stopGridAutoplay();
      this._initMobileSwiper();
    } else {
      this._destroyMobileSwiper();
      if (this.videoAutoplay) this._startGridAutoplay();
    }
  }

  _initMobileSwiper() {
    if (this._swiperContainer) return;
    const gallery = this.querySelector(".product-main__gallery");
    if (!gallery) return;

    const items = Array.from(gallery.children);
    const sc = document.createElement("swiper-container");
    sc.className = "product-main__swiper";
    sc.setAttribute("init", "false");
    items.forEach((item) => {
      const slide = document.createElement("swiper-slide");
      slide.appendChild(item);
      sc.appendChild(slide);
    });

    gallery.style.display = "none";
    gallery.after(sc);
    this._gallery = gallery;
    this._swiperContainer = sc;

    const single = items.length === 1;
    Object.assign(sc, {
      slidesPerView: 1,
      spaceBetween: 0,
      simulateTouch: !single,
      grabCursor: !single,
      touchEventsTarget: "container",
      navigation: single
        ? false
        : {
            nextEl: ".product-main__gallery-arrow--next",
            prevEl: ".product-main__gallery-arrow--prev",
          },
      pagination: single
        ? false
        : {
            el: ".product-main__gallery-pagination",
            clickable: true,
            dynamicBullets: true,
            // dynamicMainBullets: 3,
          },
      on: {
        slideChange: (swiper) => this._syncGalleryVideos(swiper.activeIndex),
      },
    });
    sc.initialize();
    this._syncGalleryVideos(0);

    this._videoClickHandler = (e) => {
      const video = e.target.closest("[data-gallery-video]");
      if (!video) return;
      const item = video.closest(".product-main__gallery-item");
      if (video.paused) {
        video.play().catch(() => {});
        item?.classList.add("is-playing");
      } else {
        video.pause();
        item?.classList.remove("is-playing");
      }
    };
    sc.addEventListener("click", this._videoClickHandler);
  }

  _destroyMobileSwiper() {
    if (!this._swiperContainer) return;
    const sc = this._swiperContainer;
    const gallery = this._gallery;
    sc.querySelectorAll("swiper-slide").forEach((slide) => {
      const item = slide.firstElementChild;
      if (item) gallery.appendChild(item);
    });
    if (this._videoClickHandler) {
      sc.removeEventListener("click", this._videoClickHandler);
      this._videoClickHandler = null;
    }
    try {
      sc.swiper?.destroy(true, true);
    } catch {}
    sc.remove();
    gallery.style.display = "";
    this._swiperContainer = null;
    this._gallery = null;
    this.querySelectorAll("[data-gallery-video]").forEach((v) => v.pause());
  }

  _syncGalleryVideos(activeIndex) {
    const slides = this.querySelectorAll(".product-main__gallery-item");
    slides.forEach((slide, i) => {
      const video = slide.querySelector("[data-gallery-video]");
      if (!video) return;
      if (i === activeIndex) {
        if (video.readyState === 0) video.load();
        video.play().catch(() => {});
        slide.classList.add("is-playing");
      } else {
        video.pause();
        slide.classList.remove("is-playing");
      }
    });
  }
}

if (!customElements.get("product-lightbox"))
  customElements.define("product-lightbox", ProductLightbox);

// ─── Product page: options, variant selection, clearance ─────────────────────

class ProductOptions extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(
      this.querySelector("[data-variants]").textContent,
    );
    this.selectedOptions = JSON.parse(
      this.querySelector("[data-selected]")?.textContent || "[]",
    );
    const sectionEl = this.closest('[id^="shopify-section-"]');
    this.sectionId = sectionEl?.id.replace("shopify-section-", "");

    this.addEventListener("option:change", (e) => {
      const { optionIndex, value } = e.detail;
      this.selectedOptions[optionIndex] = value;
      this._selectVariant();
    });
  }

  async _selectVariant() {
    const variant = this.variants.find((v) =>
      v.options.every((opt, i) => opt === this.selectedOptions[i]),
    );
    if (!variant) return;

    const form = document.getElementById(this.dataset.form);

    const idInput = form?.querySelector(".product-form__variant-id");
    if (idInput) idInput.value = variant.id;

    const submitBtn = form?.querySelector("[button-add-to-cart]");
    if (submitBtn) {
      submitBtn.disabled = !variant.available;
      const span = submitBtn.querySelector("span");
      if (span) {
        span.textContent = variant.available
          ? submitBtn.dataset.addText
          : submitBtn.dataset.soldOutText;
      }
    }

    document.querySelectorAll("[data-option-display]").forEach((el) => {
      const i = parseInt(el.dataset.optionDisplay, 10);
      if (this.selectedOptions[i] !== undefined) {
        const unit = el.dataset.unit;
        el.textContent = unit
          ? `${this.selectedOptions[i]} ${unit}`
          : this.selectedOptions[i];
      }
    });

    const url = new URL(window.location);
    url.searchParams.set("variant", variant.id);
    history.replaceState(null, "", url.toString());

    const priceEl = document.querySelector("[data-product-price]");
    if (priceEl) priceEl.classList.add("is-loading");
    try {
      await this._refreshFromSection(variant.id);
    } finally {
      if (priceEl) priceEl.classList.remove("is-loading");
    }

    window.dispatchEvent(
      new CustomEvent("variant:change", { detail: { variantId: variant.id } }),
    );
  }

  async _refreshFromSection(variantId) {
    if (!this.sectionId) return;
    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set("variant", variantId);
    url.searchParams.set("section_id", this.sectionId);
    let html;
    try {
      const res = await fetch(url.toString());
      if (!res.ok) return;
      html = await res.text();
    } catch {
      return;
    }
    const doc = new DOMParser().parseFromString(html, "text/html");

    this._swapAll(doc, "[data-product-price]", "innerHTML");
    this._swapAll(doc, "[data-connection-price]", "textContent");
    this._swapAll(doc, ".choice-list__price-diff", "textContent");
    this._swapAll(doc, "[data-discount-offer]", "innerHTML");
  }

  _swap(doc, selector, prop) {
    const fresh = doc.querySelector(selector);
    const current = document.querySelector(selector);
    if (fresh && current) current[prop] = fresh[prop];
  }

  _swapAll(doc, selector, prop) {
    const freshAll = doc.querySelectorAll(selector);
    const currentAll = document.querySelectorAll(selector);
    currentAll.forEach((el, i) => {
      if (freshAll[i]) el[prop] = freshAll[i][prop];
    });
  }
}
if (!customElements.get("product-options"))
  customElements.define("product-options", ProductOptions);

class FloatingAtc extends HTMLElement {

  connectedCallback() {
    this.atcBtn = this.querySelector("[data-floating-atc-btn]");
    this.summaryBtn = this.querySelector("[data-floating-summary]");
    this.originalBtn = document.querySelector(
      ".product-main [button-add-to-cart]",
    );
    this.optionsAnchor =
      document.querySelector(".product-main product-options") ||
      document.querySelector(".product-main__prices");

    if (!this.originalBtn) return;

    this.atcBtn?.addEventListener("click", () => this.originalBtn.click());
    this.summaryBtn?.addEventListener("click", () => this._scrollToOptions());

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this._scrolledPast =
          !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this._update();
      },
      { threshold: 0 },
    );
    this.observer.observe(this.originalBtn);

    // Hide the bar once the footer scrolls into view so it never covers it.
    // The <footer> itself is display:contents (no box), so observe its first
    // rendered child — the top of the footer's content.
    const footer = document.querySelector('footer[role="contentinfo"]');
    this.footerTarget = footer?.firstElementChild;
    if (this.footerTarget) {
      this.footerObserver = new IntersectionObserver(
        ([entry]) => {
          this._footerVisible = entry.isIntersecting;
          this._update();
        },
        { threshold: 0 },
      );
      this.footerObserver.observe(this.footerTarget);
    }

    this._onVariantChange = () => this._syncFromOriginal();
    window.addEventListener("variant:change", this._onVariantChange);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.footerObserver?.disconnect();
    if (this._onVariantChange) {
      window.removeEventListener("variant:change", this._onVariantChange);
    }
    this._setChatOffset(false);
  }

  _update() {
    const show = this._scrolledPast && !this._footerVisible;
    this.classList.toggle("is-visible", show);
    this._setChatOffset(show);
  }

  // Lift the chat bubble above the bar by its measured height (varies with
  // content) plus a gap. Shopify Inbox is a full-viewport `position: fixed;
  // inset: 0` overlay, and its activator is laid out inside that box — its own
  // `--shopify-agent-activator-offset` only drives the open panel, not the
  // bubble. So the offset raises the overlay's bottom edge instead
  // (component-floating-atc.css), which carries the bubble and panel together.
  _setChatOffset(show) {
    if (show) {
      const offset = this.offsetHeight;
      document.body.style.setProperty("--chat-bubble-offset", `${offset}px`);
    } else {
      document.body.style.removeProperty("--chat-bubble-offset");
    }
  }

  _syncFromOriginal() {
    if (!this.originalBtn || !this.atcBtn) return;
    this.atcBtn.disabled = this.originalBtn.disabled;
    const span = this.atcBtn.querySelector("span");
    if (span) {
      span.textContent = this.atcBtn.disabled
        ? this.atcBtn.dataset.soldOutText
        : this.atcBtn.dataset.addText;
    }
    if (this.classList.contains("is-visible")) this._setChatOffset(true);
  }

  _scrollToOptions() {
    if (!this.optionsAnchor) return;
    const headerOffset =
      parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--header-height",
        ),
      ) || 0;
    const top =
      this.optionsAnchor.getBoundingClientRect().top +
      window.scrollY -
      headerOffset -
      16;
    window.scrollTo({ top, behavior: "smooth" });
  }
}
if (!customElements.get("floating-atc"))
  customElements.define("floating-atc", FloatingAtc);

class ClipboardCopy extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-copy]");
      if (!btn || !this.contains(btn)) return;
      this._copy(btn.dataset.copy, btn);
      if (this.hasAttribute("data-apply-discount")) this._apply(btn.dataset.copy);
    });
  }

  _apply(code) {
    const root = window.Shopify?.routes?.root || "/";
    // Shopify stores the code for checkout and answers with a redirect; cart.js
    // is the lightest page to land on, the storefront home being the default.
    fetch(`${root}discount/${encodeURIComponent(code)}?redirect=${root}cart.js`, {
      credentials: "same-origin",
    }).catch(() => {});
  }

  _copy(text, btn) {
    const original = btn.innerHTML;
    const restore = () => {
      btn.innerHTML = original;
      btn.disabled = false;
    };
    const showFeedback = () => {
      btn.textContent = this.dataset.labelCopied;
      btn.disabled = true;
      setTimeout(restore, 2500);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(showFeedback)
        .catch(() => prompt(text));
    } else {
      prompt(text);
    }
  }
}
if (!customElements.get("clipboard-copy"))
  customElements.define("clipboard-copy", ClipboardCopy);

class DescriptionExpand extends HTMLElement {
  connectedCallback() {
    const body = this.querySelector("[data-desc-body]");
    const btn = this.querySelector("[data-desc-expand]");
    if (!body || !btn) return;

    if (body.scrollHeight <= body.clientHeight + 2) {
      body.removeAttribute("data-desc-body");
      btn.remove();
      return;
    }

    btn.addEventListener("click", () => {
      body.style.maxHeight = body.scrollHeight + "px";
      body.addEventListener(
        "transitionend",
        () => {
          body.style.maxHeight = "";
          body.removeAttribute("data-desc-body");
        },
        { once: true },
      );
      btn.remove();
    });
  }
}
if (!customElements.get("description-expand"))
  customElements.define("description-expand", DescriptionExpand);

class VariantOptionsDrawer extends DrawerElement {
  connectedCallback() {
    super.connectedCallback();
    this.optionIndex = parseInt(this.dataset.optionIndex, 10);

    this.querySelectorAll("[data-option-value]").forEach((btn) => {
      btn.addEventListener("click", () => {
        this.querySelectorAll("[data-option-value]").forEach((b) => {
          const isActive = b === btn;
          b.classList.toggle("choice-list__item--current", isActive);
          if (b.hasAttribute("aria-pressed")) {
            b.setAttribute("aria-pressed", isActive ? "true" : "false");
          }
        });
        this.dispatchEvent(
          new CustomEvent("option:change", {
            bubbles: true,
            detail: {
              optionIndex: this.optionIndex,
              value: btn.dataset.optionValue,
            },
          }),
        );
        this.close();
      });
    });
  }
}
if (!customElements.get("variant-options-drawer"))
  customElements.define("variant-options-drawer", VariantOptionsDrawer);

class ProductGroupDrawer extends DrawerElement {}
if (!customElements.get("product-group-drawer"))
  customElements.define("product-group-drawer", ProductGroupDrawer);

class CustomColorInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector("[data-custom-color-input]");
    this.inputWrap = this.querySelector("[data-custom-color-field]");
    this.display = this.querySelector("[data-custom-color-display]");
    this.editBtn = this.querySelector("[data-custom-color-edit]");
    this.errorEl = this.querySelector("[data-custom-color-error]");
    this.label = this.querySelector("[data-custom-color-label]");

    this.querySelector("[data-custom-color-confirm]")?.addEventListener(
      "click",
      () => this._confirm(),
    );
    this.input?.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        this._confirm();
      }
    });
    this.editBtn?.addEventListener("click", () => this._edit());

    this.addEventListener("custom-color:required", () => {
      this._edit();
      this._showError(this.errorEl?.dataset.requiredMsg || "");
    });
  }

  _confirm() {
    if (!this.input) return;
    const val = this.input.value.trim();

    if (!val) {
      this._showError(this.errorEl?.dataset.requiredMsg || "");
      this.input.focus();
      return;
    }

    this.input.value = val;
    if (this.display) this.display.textContent = val;
    if (this.inputWrap) this.inputWrap.classList.add("hidden");
    if (this.editBtn) this.editBtn.classList.remove("hidden");
    if (this.label) this.label.textContent = this.dataset.labelConfirmed || "";
    this.removeAttribute("data-custom-color-pending");

    // Soft format warning — doesn't block submission
    // RAL Classic (RAL 9010), Design (RAL 070 80 10), Effect (RAL 490-5, RAL 110-M)
    // NCS (S 1050-Y90R, NCS S 0300-N)
    const isValidFormat =
      /^RAL\s?\d{3,4}(\s\d{2}){0,2}(-[\dM])?$/i.test(val) ||
      /^(NCS\s?)?S\s?\d{4}-[A-Z\d]+$/i.test(val);
    if (!isValidFormat) {
      this._showError(this.errorEl?.dataset.formatMsg || "", "warning");
    } else {
      this._hideError();
    }
  }

  _edit() {
    if (this.inputWrap) this.inputWrap.classList.remove("hidden");
    if (this.editBtn) this.editBtn.classList.add("hidden");
    if (this.label) this.label.textContent = this.dataset.labelPending || "";
    this.setAttribute("data-custom-color-pending", "");
    this._hideError();
    setTimeout(() => this.input?.focus(), 50);
  }

  _showError(msg, type = "error") {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg;
    this.errorEl.classList.remove("hidden");
    this.errorEl.classList.toggle(
      "product-main__custom-color-error--warning",
      type === "warning",
    );
  }

  _hideError() {
    if (!this.errorEl) return;
    this.errorEl.textContent = "";
    this.errorEl.classList.add("hidden");
    this.errorEl.classList.remove("product-main__custom-color-error--warning");
  }
}
if (!customElements.get("custom-color-input"))
  customElements.define("custom-color-input", CustomColorInput);

class ClearanceOption extends HTMLElement {
  connectedCallback() {
    this.optionIndex = parseInt(this.dataset.optionIndex, 10);
    this.inputWrap = this.querySelector("[data-clearance-input-wrap]");
    this.propertyInput = this.querySelector("[data-clearance-input]");
    this.display = this.querySelector("[data-clearance-display]");
    this.errorEl = this.querySelector("[data-clearance-error]");
    this.editBtn = this.querySelector("[data-clearance-edit]");
    this.confirmed = false;

    const preselectedCustom = this.querySelector(
      "[data-clearance-custom].is-selected",
    );
    if (preselectedCustom) {
      this.setAttribute("data-clearance-pending", "");
    }

    this.querySelectorAll("[data-clearance-value]").forEach((btn) => {
      btn.addEventListener("click", () => this._select(btn));
    });

    this.querySelector("[data-clearance-confirm]")?.addEventListener(
      "click",
      () => this._confirm(),
    );
    this.propertyInput?.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        this._confirm();
      }
    });
    this.editBtn?.addEventListener("click", () => this._reopen());

    this.addEventListener("clearance:required", () => {
      this._reopen();
      if (this.errorEl) {
        this.errorEl.textContent =
          this.errorEl.dataset.clearanceRequiredMsg || "";
        this.errorEl.classList.remove("hidden");
      }
    });
  }

  _select(btn) {
    const value = btn.dataset.clearanceValue;
    const isCustom = "clearanceCustom" in btn.dataset;

    this.querySelectorAll("[data-clearance-value]").forEach((b) => {
      b.classList.toggle("is-selected", b === btn);
    });

    if (this.inputWrap) this.inputWrap.classList.toggle("is-open", isCustom);
    if (this.propertyInput) {
      this.propertyInput.disabled = !isCustom;
      if (isCustom) {
        const [, range] = value.split("__");
        if (range) {
          const [min, max] = range.split("_");
          if (min) this.propertyInput.min = min;
          if (max) this.propertyInput.max = max;
          const rangeLabel = this.querySelector("[data-clearance-range-label]");
          if (rangeLabel) rangeLabel.textContent = `${min}–${max}`;
        }
        setTimeout(() => this.propertyInput.focus(), 310);
      }
    }

    if (this.display) {
      const clone = btn.cloneNode(true);
      clone.querySelector(".choice-list__price-diff")?.remove();
      this._setDisplay(isCustom ? clone.textContent.trim() : `${value} cm`);
    }

    this.confirmed = false;
    if (isCustom) {
      this.setAttribute("data-clearance-pending", "");
    } else {
      this.removeAttribute("data-clearance-pending");
    }
    if (this.editBtn) this.editBtn.classList.add("hidden");
    if (this.errorEl) this.errorEl.classList.add("hidden");

    this.dispatchEvent(
      new CustomEvent("option:change", {
        bubbles: true,
        detail: { optionIndex: this.optionIndex, value },
      }),
    );
  }

  _confirm() {
    if (!this.propertyInput) return;
    const val = parseFloat(this.propertyInput.value);
    const min = parseFloat(this.propertyInput.min);
    const max = parseFloat(this.propertyInput.max);

    if (!this.propertyInput.value || isNaN(val) || val < min || val > max) {
      if (this.errorEl) {
        const template =
          this.errorEl.dataset.clearanceErrorMsg || `${min}–${max} cm`;
        this.errorEl.textContent = template
          .replace("%min%", min)
          .replace("%max%", max);
        this.errorEl.classList.remove("hidden");
      }
      this.propertyInput.focus();
      return;
    }

    if (this.errorEl) this.errorEl.classList.add("hidden");
    if (this.display) this._setDisplay(`${val} cm`);
    if (this.inputWrap) this.inputWrap.classList.remove("is-open");
    this.confirmed = true;
    this.removeAttribute("data-clearance-pending");
    if (this.editBtn) this.editBtn.classList.remove("hidden");
  }

  _reopen() {
    const customBtn = this.querySelector("[data-clearance-custom]");
    if (customBtn) this._select(customBtn);
  }

  _setDisplay(text) {
    document.querySelectorAll("[data-clearance-display]").forEach((el) => {
      el.textContent = text;
    });
  }
}
if (!customElements.get("clearance-option"))
  customElements.define("clearance-option", ClearanceOption);

class DimensionTable extends HTMLElement {
  connectedCallback() {
    this.mattressIndex = parseInt(this.dataset.mattressIndex, 10);

    this.querySelectorAll("td").forEach((td) => {
      td.dataset.template = td.textContent;
    });

    this._resolve(this.dataset.mattressValue);

    this._handler = (e) => {
      if (e.detail.optionIndex === this.mattressIndex) {
        this._resolve(e.detail.value);
      }
    };
    document.addEventListener("option:change", this._handler);
  }

  disconnectedCallback() {
    document.removeEventListener("option:change", this._handler);
  }

  _resolve(sizeStr) {
    if (!sizeStr || this.mattressIndex < 0) return;
    const parts = sizeStr.split("x");
    if (parts.length < 2) return;
    const width = parseFloat(parts[0]);
    const length = parseFloat(parts[1]);
    if (isNaN(width) || isNaN(length)) return;
    const dims = { width, length };

    this.querySelectorAll("td").forEach((td) => {
      const template = td.dataset.template;
      td.textContent = template.replace(
        /\[\[(\w+)([+-]\d+(?:\.\d+)?)?\]\]/g,
        (match, name, offset) => {
          const base = dims[name];
          if (base === undefined) return match;
          return String(base + (offset ? parseFloat(offset) : 0));
        },
      );
    });
  }
}
if (!customElements.get("dimension-table"))
  customElements.define("dimension-table", DimensionTable);

// ─── Dimension image swap ─────────────────────────────────────────────────────

class DimensionTabs extends HTMLElement {
  connectedCallback() {
    this._clearanceIndex = parseInt(this.dataset.clearanceIndex, 10);

    this.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-tab]");
      if (btn) this._switchTab(Number(btn.dataset.tab));
    });

    if (!isNaN(this._clearanceIndex) && this._clearanceIndex >= 0) {
      this._optHandler = (e) => {
        if (e.detail.optionIndex === this._clearanceIndex)
          this._switchTab(e.detail.value.startsWith("custom__") ? 1 : 0);
      };
      document.addEventListener("option:change", this._optHandler);
    }

    const initial = parseInt(this.dataset.initialTab, 10);
    if (initial > 0) this._switchTab(initial);
  }

  disconnectedCallback() {
    if (this._optHandler)
      document.removeEventListener("option:change", this._optHandler);
  }

  _switchTab(idx) {
    this.querySelectorAll(".dimension-tabs__panel").forEach((p, i) => {
      p.hidden = i !== idx;
    });
    this.querySelectorAll(".dimension-tabs__tab").forEach((t, i) => {
      t.classList.toggle("is-active", i === idx);
      t.setAttribute("aria-selected", String(i === idx));
    });
  }
}
if (!customElements.get("dimension-tabs"))
  customElements.define("dimension-tabs", DimensionTabs);

// ─── Breadcrumb expander ──────────────────────────────────────────────────────

class BreadcrumbExpander extends HTMLElement {
  connectedCallback() {
    const btn = this.querySelector("[data-breadcrumb-expand]");
    btn?.addEventListener("click", () => {
      this.classList.add("is-expanded");
      btn.setAttribute("aria-expanded", "true");
    });
  }
}
if (!customElements.get("breadcrumb-expander"))
  customElements.define("breadcrumb-expander", BreadcrumbExpander);

// ─── Wishlist Counter (MiB Wishlist — header badge) ──────────────────────────

class WishlistCounter extends HTMLElement {
  connectedCallback() {
    this._indicator = this.querySelector("[data-wishlist-indicator]");
    this._handler = (e) => this._update(e?.detail?.count ?? 0);
    window.addEventListener("mib:wishlist:items", this._handler);
  }

  disconnectedCallback() {
    window.removeEventListener("mib:wishlist:items", this._handler);
  }

  _update(count) {
    if (!this._indicator) return;
    const n = typeof count === "number" ? count : 0;
    this._indicator.setAttribute("data-wishlist-indicator", String(n));
    this._indicator.textContent = String(n);
  }
}
if (!customElements.get("wishlist-counter"))
  customElements.define("wishlist-counter", WishlistCounter);

// ─── Wishlist Button (MiB Wishlist) ──────────────────────────────────────────

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this._btn = this.querySelector(
      ".mib-wishlist-btn, .mib-global-wishlist-btn",
    );
    if (!this._btn) return;

    this._status = document.createElement("span");
    this._status.className = "visually-hidden";
    this._status.setAttribute("aria-live", "polite");
    this._btn.appendChild(this._status);
    this._applyState();

    this._mo = new MutationObserver(() => this._applyState());
    this._mo.observe(this._btn, {
      attributes: true,
      attributeFilter: [
        "data-mib-wishlist-active",
        "data-mib-wishlist-count",
        "data-mib-wishlist-loading",
      ],
    });

    this._clickHandler = (e) => {
      if (this._btn.dataset.mibClickLock === "1") {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    this._btn.addEventListener("click", this._clickHandler, true);

    this._variantHandler = () => this._syncVariantFromUrl();
    window.addEventListener("wishlist:variant-change", this._variantHandler);
    this._syncVariantFromUrl();
  }

  disconnectedCallback() {
    this._mo?.disconnect();
    this._btn?.removeEventListener("click", this._clickHandler, true);
    window.removeEventListener("wishlist:variant-change", this._variantHandler);
  }

  _applyState() {
    const btn = this._btn;
    const label = btn.querySelector(".mib-wishlist-label");
    const countEl = btn.querySelector(".mib-wishlist-count");
    const active = btn.getAttribute("data-mib-wishlist-active") === "true";
    const loading = btn.getAttribute("data-mib-wishlist-loading") === "true";
    const count = btn.getAttribute("data-mib-wishlist-count");

    if (label) {
      label.textContent = active
        ? (btn.dataset.labelActive ?? "")
        : (btn.dataset.labelInactive ?? "");
    }

    if (countEl) {
      const show = !loading && btn.hasAttribute("data-mib-wishlist-count");
      countEl.textContent = show ? `(${count ?? "0"})` : "";
      countEl.setAttribute("aria-hidden", show ? "false" : "true");
    }

    if (btn.dataset.labelActive && btn.dataset.labelInactive) {
      const newLabel = active
        ? btn.dataset.labelActive
        : btn.dataset.labelInactive;
      btn.setAttribute("aria-label", newLabel);
      if (this._status) this._status.textContent = newLabel;
    }

    btn.toggleAttribute("data-loading", loading);

    btn.toggleAttribute("aria-disabled", loading);
    btn.setAttribute("aria-busy", loading ? "true" : "false");

    if (loading) btn.dataset.mibClickLock = "1";
    else delete btn.dataset.mibClickLock;
  }

  _syncVariantFromUrl() {
    try {
      const variantId = new URLSearchParams(window.location.search)
        .get("variant")
        ?.trim();
      if (
        !variantId ||
        this._btn.getAttribute("data-mib-variant-id") === variantId
      )
        return;
      this._btn.setAttribute("data-mib-variant-id", variantId);
    } catch {}
  }
}
if (!customElements.get("wishlist-button"))
  customElements.define("wishlist-button", WishlistButton);

// ─── Wishlist Grid (MiB Wishlist page) ───────────────────────────────────────

class WishlistGrid extends HTMLElement {
  connectedCallback() {
    this._grid = this.querySelector(".mib-wishlist-grid");
    if (!this._grid) return;

    this._previousKeys = new Set();
    this._grid.innerHTML = this._spinnerHtml();
    this._grid.setAttribute("data-mib-wishlist-loading", "true");

    const shareCode = new URLSearchParams(location.search).get("share")?.trim();
    if (shareCode) {
      const btn = this.querySelector("[data-mib-wishlist-share]");
      if (btn) {
        this._shareBtn = btn;
        btn.removeAttribute("data-mib-wishlist-share");
        btn.textContent = this.dataset.labelCopyUrl;
        btn.addEventListener("click", () =>
          this._copyToClipboard(window.location.href, btn),
        );
      }
      this._loadSharedWishlist(shareCode, Date.now());
    } else {
      this._itemsHandler = (e) => this._apply(e?.detail);
      this._shareHandler = (e) => this._handleShare(e?.detail);
      window.addEventListener("mib:wishlist:items", this._itemsHandler);
      window.addEventListener("mib:wishlist:share-ready", this._shareHandler);

      const shownAt = Date.now();
      requestAnimationFrame(() => {
        const wait = Math.max(0, 400 - (Date.now() - shownAt));
        const run = () => {
          const snapshot = window.MIB_Wishlist?.getSnapshot?.();
          if (snapshot != null) this._apply(snapshot);
        };
        if (wait > 0) setTimeout(run, wait);
        else run();
      });
    }
  }

  disconnectedCallback() {
    window.removeEventListener("mib:wishlist:items", this._itemsHandler);
    window.removeEventListener("mib:wishlist:share-ready", this._shareHandler);
  }

  _loadSharedWishlist(code, startMs) {
    const api = window.MIB_Wishlist?.getSharedWishlistByCode;
    if (typeof api !== "function") {
      if (Date.now() - startMs > 8000) {
        this._grid.removeAttribute("data-mib-wishlist-loading");
        this._grid.innerHTML = this._sharedErrorHtml();
        return;
      }
      setTimeout(() => this._loadSharedWishlist(code, startMs), 150);
      return;
    }
    api(code)
      .then((data) => {
        this._grid.removeAttribute("data-mib-wishlist-loading");
        const items = Array.isArray(data?.items) ? data.items : [];
        this._grid.innerHTML =
          items.length === 0
            ? this._sharedEmptyHtml()
            : items.map((item) => this._cardHtml(item, true)).join("");
      })
      .catch(() => {
        this._grid.removeAttribute("data-mib-wishlist-loading");
        this._grid.innerHTML = this._sharedErrorHtml();
      });
  }

  _apply(source) {
    const loading = source?.loading === true;
    this._grid.setAttribute(
      "data-mib-wishlist-loading",
      loading ? "true" : "false",
    );
    if (loading) {
      if (this._previousKeys.size === 0)
        this._grid.innerHTML = this._spinnerHtml();
      return;
    }

    const items = Array.isArray(source?.items) ? source.items : [];
    const newKeys = new Set(items.map((i) => this._key(i)));

    // Single-item removal — animate out without full re-render
    const removedKey =
      this._previousKeys.size === newKeys.size + 1 &&
      [...this._previousKeys].find((k) => !newKeys.has(k));

    if (removedKey) {
      for (const card of this._grid.querySelectorAll(".mib-wishlist-card")) {
        if (
          this._key({
            productId: card.dataset.mibProductId,
            variantId: card.dataset.mibVariantId,
          }) === removedKey
        ) {
          card.remove();
          this._previousKeys = newKeys;
          if (this._previousKeys.size === 0)
            this._grid.innerHTML = this._emptyHtml();
          return;
        }
      }
    }

    this._renderItems(items);
  }

  _renderItems(items) {
    this._previousKeys = new Set(items.map((i) => this._key(i)));
    this._grid.innerHTML =
      items.length === 0
        ? this._emptyHtml()
        : items.map((item) => this._cardHtml(item)).join("");
  }

  _key(item) {
    return `${item?.productId ?? ""}|${item?.variantId ?? ""}`;
  }

  _esc(s) {
    return String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  _spinnerHtml() {
    return `<div class="mib-wishlist-loading-spinner"><div class="mib-wishlist-spinner" aria-hidden="true"></div></div>`;
  }

  _emptyHtml() {
    const e = this._esc.bind(this);
    return `<div class="mib-wishlist-empty-state">
    <svg class="mib-wishlist-empty-state__icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10.4107 19.9677C7.58942 17.858 2 13.0348 2 8.69444C2 5.82563 4.10526 3.5 7 3.5C8.5 3.5 10 4 12 6C14 4 15.5 3.5 17 3.5C19.8947 3.5 22 5.82563 22 8.69444C22 13.0348 16.4106 17.858 13.5893 19.9677C12.6399 20.6776 11.3601 20.6776 10.4107 19.9677Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
      <p class="mib-wishlist-empty-state__title">${e(this.dataset.emptyTitle)}</p>
      <p class="mib-wishlist-empty-state__text">${e(this.dataset.emptyText)}</p>
    </div>`;
  }

  _cardHtml(item, readOnly = false) {
    const e = this._esc.bind(this);
    return `<article class="mib-wishlist-card" role="listitem" data-mib-product-id="${e(item.productId)}" data-mib-variant-id="${e(item.variantId ?? "")}">
      <a class="mib-wishlist-card__image" href="/products/${e(item.productHandle)}">
        <img src="${e(item.image)}" alt="${e(item.productTitle)}" loading="lazy">
      </a>
      <div class="mib-wishlist-card__info">
        <h3 class="mib-wishlist-card__title">${e(item.productTitle)}</h3>
        ${item.variantTitle ? `<p class="mib-wishlist-card__variant">${e(item.variantTitle)}</p>` : ""}
        <p class="mib-wishlist-card__price">${item.price ?? ""}</p>
      </div>
      <div class="mib-wishlist-card__actions">
        <a href="/products/${e(item.productHandle)}" class="btn btn--primary">${e(this.dataset.labelView)}</a>
        ${readOnly ? "" : `<button type="button" class="btn btn--secondary mib-wishlist-remove" data-mib-wishlist-remove data-mib-product-id="${e(item.productId)}" data-mib-variant-id="${e(item.variantId ?? "")}">${e(this.dataset.labelRemove)}</button>`}
      </div>
    </article>`;
  }

  _sharedEmptyHtml() {
    const e = this._esc.bind(this);
    return `<div class="mib-wishlist-empty-state">
      <svg class="mib-wishlist-empty-state__icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M10.4107 19.9677C7.58942 17.858 2 13.0348 2 8.69444C2 5.82563 4.10526 3.5 7 3.5C8.5 3.5 10 4 12 6C14 4 15.5 3.5 17 3.5C19.8947 3.5 22 5.82563 22 8.69444C22 13.0348 16.4106 17.858 13.5893 19.9677C12.6399 20.6776 11.3601 20.6776 10.4107 19.9677Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <p class="mib-wishlist-empty-state__title">${e(this.dataset.sharedEmptyTitle)}</p>
      <p class="mib-wishlist-empty-state__text">${e(this.dataset.sharedEmptyText)}</p>
    </div>`;
  }

  _sharedErrorHtml() {
    const e = this._esc.bind(this);
    return `<div class="mib-wishlist-empty-state mib-wishlist-empty-state--error">
      <svg class="mib-wishlist-empty-state__icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5"/>
        <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        <circle cx="12" cy="16" r=".5" fill="currentColor" stroke="currentColor" stroke-width="1"/>
      </svg>
      <p class="mib-wishlist-empty-state__title">${e(this.dataset.sharedErrorTitle)}</p>
      <p class="mib-wishlist-empty-state__text">${e(this.dataset.sharedErrorText)}</p>
    </div>`;
  }

  _handleShare({ shareCode } = {}) {
    if (!shareCode) return;
    const pagePath =
      this.dataset.wishlistPath || window.location.pathname.slice(1);
    const link =
      typeof window.MIB_Wishlist?.getShareLinkForCustomPage === "function"
        ? window.MIB_Wishlist.getShareLinkForCustomPage(pagePath, shareCode)
        : shareCode;
    if (!link) return;
    const btn =
      this.querySelector("[data-mib-wishlist-share]") ??
      this.closest("drawer-element")?.querySelector(
        "[data-mib-wishlist-share]",
      );
    this._copyToClipboard(link, btn);
  }

  _copyToClipboard(text, btn) {
    const originalHtml = btn?.innerHTML;
    const showFeedback = () => {
      if (!btn) return;
      btn.textContent = this.dataset.labelCopied;
      btn.disabled = true;
      setTimeout(() => {
        btn.innerHTML = originalHtml;
        btn.disabled = false;
      }, 2500);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(showFeedback)
        .catch(() => prompt(text));
    } else {
      prompt(text);
    }
  }
}
if (!customElements.get("wishlist-grid"))
  customElements.define("wishlist-grid", WishlistGrid);

// Broadcast URL variant changes to all wishlist-button instances
{
  const notify = () =>
    window.dispatchEvent(new CustomEvent("wishlist:variant-change"));
  const _push = history.pushState.bind(history);
  const _replace = history.replaceState.bind(history);
  history.pushState = (...args) => {
    _push(...args);
    notify();
  };
  history.replaceState = (...args) => {
    _replace(...args);
    notify();
  };
  window.addEventListener("popstate", notify);
}

class ArticleToc extends HTMLElement {
  connectedCallback() {
    const selector = this.dataset.contentSelector || ".article__content";
    this.content = document.querySelector(selector);
    this.list = this.querySelector(".article-toc__list");
    if (!this.content || !this.list) return;

    this.headings = Array.from(this.content.querySelectorAll("h2, h3"));

    // Append an out-of-content section (e.g. in-article featured products) as the last entry.
    const extraSelector = this.dataset.extraSelector;
    if (extraSelector) {
      const extraHeading = document
        .querySelector(extraSelector)
        ?.querySelector("h2, h3");
      if (extraHeading) this.headings.push(extraHeading);
    }

    if (this.headings.length === 0) return;

    this.links = new Map();
    this.headings.forEach((heading) => {
      if (!heading.id) heading.id = this._slugify(heading.textContent);

      const li = document.createElement("li");
      li.className = `article-toc__item article-toc__item--${heading.tagName.toLowerCase()}`;

      const link = document.createElement("a");
      link.className = "article-toc__link";
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent;
      link.addEventListener("click", this._onClick.bind(this));

      li.appendChild(link);
      this.list.appendChild(li);
      this.links.set(heading.id, link);
    });

    this.hidden = false;

    // Highlight the first entry on load so the TOC never starts blank.
    this._activate(this.headings[0].id);

    this.observer = new IntersectionObserver(this._onIntersect.bind(this), {
      rootMargin: "0px 0px -70% 0px",
      threshold: 0,
    });
    this.headings.forEach((heading) => this.observer.observe(heading));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  _onIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) this._activate(entry.target.id);
    });
  }

  _activate(id) {
    this.links.forEach((link, key) => {
      const active = key === id;
      link.classList.toggle("is-active", active);
      if (active) {
        link.setAttribute("aria-current", "true");
      } else {
        link.removeAttribute("aria-current");
      }
    });
  }

  _onClick(event) {
    const id = event.currentTarget.getAttribute("href").slice(1);
    const target = document.getElementById(id);
    if (!target) return;

    event.preventDefault();
    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    target.scrollIntoView({
      behavior: reduce ? "auto" : "smooth",
      block: "start",
    });
    history.replaceState(null, "", `#${id}`);
  }

  _slugify(text) {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\p{L}\p{N}]+/gu, "-")
      .replace(/^-+|-+$/g, "");
  }
}
if (!customElements.get("article-toc"))
  customElements.define("article-toc", ArticleToc);

class ShareLink extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector("button");
    this.button?.addEventListener("click", () => this._share());
  }

  async _share() {
    const url = this.dataset.url || location.href;
    const title = this.dataset.title || document.title;

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch {
        // User dismissed the share sheet — nothing to do.
      }
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
      const original = this.button.textContent;
      this.button.textContent = this.dataset.copied || original;
      setTimeout(() => {
        this.button.textContent = original;
      }, 2000);
    } catch {
      // Clipboard unavailable (e.g. insecure context) — fail silently.
    }
  }
}
if (!customElements.get("share-link"))
  customElements.define("share-link", ShareLink);

class ArticleProducts extends HTMLElement {
  connectedCallback() {
    const swiper = this.querySelector("swiper-container");
    if (!swiper) return;

    initRecommendationsSwiper(swiper, {
      container: this,
      navPrev: ".article__featured-products-nav .carousel-navigation__prev",
      navNext: ".article__featured-products-nav .carousel-navigation__next",
      breakpoints: {
        520: { slidesPerView: 2.5 },
        992: { slidesPerView: 3.5, spaceBetween: 24 },
      },
    });
  }
}
if (!customElements.get("article-products"))
  customElements.define("article-products", ArticleProducts);
